import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, TextInput, Alert, Image, Modal, ActivityIndicator,
  DeviceEventEmitter, RefreshControl, Keyboard, Platform, StatusBar,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { colors, spacing, radius } from '../theme';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import { usePremiumGuard } from '../hooks/usePremiumGuard';
import { AWS_CONFIG } from '../config/aws_config';
import { getWardrobe, getCatalog } from '../services/DatabaseService';
// v1.12.906 — Imports Zalando/ASOS/H&M retirés (partenaires obsolètes).
// Source unique : /catalog API via DatabaseService.getCatalog().
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { incrementAIUsage } from '../services/PremiumService';
import { applySkimlinksToProducts } from '../services/SkimlinksService';
import { normalizedNameKey } from '../utils/productNameUtils';
import { mergeDuplicateProducts } from '../utils/mergeProducts';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import ProductDetailModal from '../components/ProductDetailModal';
import DatePickerModal from '../components/DatePickerModal';
import AsyncStorage from '@react-native-async-storage/async-storage';
const API = AWS_CONFIG.apiUrl;
import { AITip, Divider, ScoreBar, CTAButton } from '../components/UI';
import PremiumModal from '../components/PremiumModal';
import UpgradeModal from '../components/UpgradeModal';
import OutfitCard from '../components/OutfitCard';
import WardrobeItemDetailModal from '../components/WardrobeItemDetailModal';
// ═══ v1.10.26 : Modal essayage virtuel (Try-on FASHN) ═══
import TryOnModal from '../components/TryOnModal';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n';
import {
  initialTrips, TRIP_STYLES, WEATHER_PROFILES,
  DONT_FORGET_BY_STYLE, generateTravelLooks, getChecklistForStyle,
} from '../data/travelData';
import {
  scoreOutfit, detectMissingPieces, findCatalogSuggestions,
  analyzeWardrobeInBackground, getOutfitCatalogSuggestions,
} from '../services/AIStylistService';
import { suggestCatalogItems } from '../services/OutfitMatchingService';
// v1.12.906 — fetchDynamicCatalog retiré (legacy : Cashmere + iQueens scraping
// via CJ direct, remplacé par /catalog API qui lit partner_products DB).
// v1.12.102 — algo Looks pour Voyage : OutfitEngine + ColorBank/MaterialBank
import { generateOutfits as OutfitEngineGenerate } from '../services/OutfitEngine';
import { getColorBank } from '../services/ColorBankService';
import { getMaterialBank } from '../services/MaterialBankService';

// ═══ Helpers : formatage des dates de voyage ═══
// La DB Postgres peut renvoyer 'YYYY-MM-DD' ou 'YYYY-MM-DDTHH:mm:ss.sssZ'
// (Date object JSON-stringifié). On normalise et on affiche en français.
const _MONTHS_FR = ['janvier','février','mars','avril','mai','juin',
  'juillet','août','septembre','octobre','novembre','décembre'];

function _parseTripDate(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      // Construire une Date locale (sans souci de fuseau)
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

// Date seule en français : "2 avril 2026"
function formatTripDate(value) {
  const d = _parseTripDate(value);
  if (!d) return '';
  return `${d.getDate()} ${_MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

// Plage : "2 → 12 avril 2026" si même mois/année, sinon "2 avril → 12 mai 2026"
// si même année, sinon "2 avril 2026 → 12 mai 2027"
function formatTripDateRange(start, end) {
  const ds = _parseTripDate(start);
  const de = _parseTripDate(end);
  if (!ds && !de) return '';
  if (ds && !de) return formatTripDate(start);
  if (!ds && de) return formatTripDate(end);
  if (ds.getFullYear() === de.getFullYear() && ds.getMonth() === de.getMonth()) {
    return `${ds.getDate()} → ${de.getDate()} ${_MONTHS_FR[de.getMonth()]} ${de.getFullYear()}`;
  }
  if (ds.getFullYear() === de.getFullYear()) {
    return `${ds.getDate()} ${_MONTHS_FR[ds.getMonth()]} → ${de.getDate()} ${_MONTHS_FR[de.getMonth()]} ${de.getFullYear()}`;
  }
  return `${formatTripDate(start)} → ${formatTripDate(end)}`;
}

// Mois + année compact : "avril 2026" (utilisé pour l'en-tête météo)
function formatTripMonth(value) {
  const d = _parseTripDate(value);
  if (!d) return '';
  return `${_MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

/* ══ Écran principal ════════════════════════════════════════ */
export default function TravelWardrobeScreen({ navigation }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  // ═══ Mode sombre ═══
  const { dark, theme } = useTheme();
  const s = useMemo(() => makeStyles(theme, dark), [theme, dark]);
  // ═══ FIX 2026-04 : safe-area Android ═══
  // SafeAreaView ne couvre pas la status bar Android par défaut → le titre
  // se superposait à l'horloge système. On ajoute un padding manuel basé
  // sur insets.top + StatusBar.currentHeight comme dans FavoritesScreen.
  const insets = useSafeAreaInsets();
  const topPadding = Platform.OS === 'android'
    ? Math.max(insets.top, StatusBar.currentHeight || 24)
    : Math.max(insets.top, 0);
  const {
    showPremium: showTravelPremium,
    limitInfo,
    guardCheck: travelGuard,
    closePremium: closeTravelPremium,
    setShowPremium: setShowTravelPremium,
    setPremiumTrigger: setTravelPremiumTrigger,
  } = usePremiumGuard(user);
  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();

  // ═══ Vérification accès Voyage (Premium uniquement) ═══
  const [travelAccess, setTravelAccess] = useState(null); // null = checking, true/false = result
  useEffect(() => {
    (async () => {
      try {
        const { hasFeatureAccess } = require('../services/PremiumService');
        const allowed = await hasFeatureAccess(user, 'travel');
        setTravelAccess(allowed);
      } catch { setTravelAccess(true); /* fail-open */ }
    })();
  }, [user?.id, user?.tier, user?.plan]);

  const [trips,       setTrips]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeTrip,  setActiveTrip]  = useState(null);
  const [view,        setView]        = useState('list');
  const [newTripData, setNewTripData] = useState({ name:'', destination:'', style:'city', styles:['city'], weather:'temperate', days:5, departure:'', returnDate:'', destLat:null, destLon:null, estimatedTemp:null });
  // v1.12.135 — Edition d'un voyage existant : si non-null, le formulaire fait PUT au lieu de POST
  const [editingTripId, setEditingTripId] = useState(null);

  const TRIPS_KEY = `outfit_trips_${user?.id || 'local'}`;

  // Persister la liste dans AsyncStorage
  const persistTrips = useCallback(async (list) => {
    try { await AsyncStorage.setItem(TRIPS_KEY, JSON.stringify(list)); } catch(e) {}
  }, [TRIPS_KEY]);

  const loadTrips = useCallback(async () => {
    setLoading(true);

    // 1. AsyncStorage en premier (toujours disponible)
    try {
      const raw = await AsyncStorage.getItem(TRIPS_KEY);
      if (raw) {
        const local = JSON.parse(raw);
        if (Array.isArray(local) && local.length > 0) {
          // Rafraîchir les packedItems depuis AsyncStorage
          const withPacked = await Promise.all(local.map(async tr => {
            let packedItems = tr.packedItems || [];
            try {
              const rawPacked = await AsyncStorage.getItem(`outfit_trip_packed_${tr.id}`);
              if (rawPacked) packedItems = JSON.parse(rawPacked);
            } catch(e) {}
            return { ...tr, packedItems };
          }));
          setTrips(withPacked);
          setLoading(false);
        }
      }
    } catch(e) {}

    // 2. DB en arrière-plan (synchro si Lambda déployé)
    if (user?.accessToken) {
      try {
        const res = await fetch(`${API}/trips`, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          const rawTrips = Array.isArray(data) ? data : [];
          if (rawTrips.length > 0) {
            const mapped = await Promise.all(rawTrips.map(async r => {
              let packedItems = [];
              try {
                const raw = await AsyncStorage.getItem(`outfit_trip_packed_${r.id}`);
                if (raw) packedItems = JSON.parse(raw);
              } catch(e) {}
              return {
                id:              r.id,
                name:            r.notes || r.destination,
                destination:     r.destination,
                destinationFlag: '✈️',
                style:           r.style || 'city',
                styles:          Array.isArray(r.styles) && r.styles.length ? r.styles : (r.style ? [r.style] : ['city']),
                weather:         r.weather || 'temperate',
                days:            r.nights || 5,
                departure:       r.start_date || '',
                returnDate:      r.end_date || '',
                packedItems,
                travelLooks:     [],
              };
            }));
            setTrips(mapped);
            await persistTrips(mapped);
          }
        }
      } catch(e) {} // silencieux si Lambda pas dispo
    }
    setLoading(false);
  }, [user, TRIPS_KEY, persistTrips]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const openTrip = (trip) => { setActiveTrip(trip); setView('trip'); };
  const goBack   = ()     => { setActiveTrip(null); setView('list'); };

  // Pre-remplit le formulaire avec les donnees d'un voyage existant pour edition
  const startEditTrip = (trip) => {
    setEditingTripId(trip.id);
    setNewTripData({
      name:           trip.name || '',
      destination:    trip.destination || '',
      style:          trip.style || 'city',
      styles:         Array.isArray(trip.styles) && trip.styles.length ? trip.styles : (trip.style ? [trip.style] : ['city']),
      weather:        trip.weather || 'temperate',
      days:           trip.days || 5,
      departure:      trip.departure || '',
      returnDate:     trip.returnDate || '',
      destLat:        trip.destLat || null,
      destLon:        trip.destLon || null,
      estimatedTemp:  trip.estimatedTemp || null,
    });
    setView('new');
  };

  const createTrip = async () => {
    if (!newTripData.name || !newTripData.destination) {
      Alert.alert(t('travel_required_fields_title'), t('travel_required_fields_msg'));
      return;
    }

    // ═══ Mode EDITION : PUT /trips/:id, pas de guard limite (le voyage existe déjà) ═══
    if (editingTripId) {
      const updatedFields = {
        name:           newTripData.name,
        destination:    newTripData.destination,
        style:          newTripData.style,
        styles:         Array.isArray(newTripData.styles) && newTripData.styles.length
                          ? newTripData.styles
                          : (newTripData.style ? [newTripData.style] : ['city']),
        weather:        newTripData.weather,
        days:           parseInt(newTripData.days) || 5,
        departure:      newTripData.departure || '',
        returnDate:     newTripData.returnDate || '',
        destLat:        newTripData.destLat || null,
        destLon:        newTripData.destLon || null,
        estimatedTemp:  newTripData.estimatedTemp || null,
      };
      const newList = trips.map(tr => tr.id === editingTripId ? { ...tr, ...updatedFields } : tr);
      setTrips(newList);
      await persistTrips(newList);
      // Si le voyage modifie est ouvert, mettre a jour activeTrip aussi
      setActiveTrip(prev => prev?.id === editingTripId ? { ...prev, ...updatedFields } : prev);
      setView('list');
      const editedId = editingTripId;
      setEditingTripId(null);
      setNewTripData({ name:'', destination:'', style:'city', styles:['city'], weather:'temperate', days:5, departure:'', returnDate:'', destLat:null, destLon:null, estimatedTemp:null });

      // Synchro DB en arriere-plan (skip pour les trips locaux pas encore persistes)
      if (user?.accessToken && !editedId.startsWith('trip_')) {
        try {
          await fetch(`${API}/trips/${editedId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.accessToken}` },
            body: JSON.stringify(updatedFields),
          });
        } catch(e) { console.warn('Trip DB update failed (saved locally):', e.message); }
      }
      return;
    }

    // ═══ GUARD : vérifier la limite de voyages du tier ═══
    const tripOk = await travelGuard('trips', trips.length, false);
    if (!tripOk) {
      console.log(`[Travel] ⛔ limite voyages atteinte (${trips.length})`);
      return; // le popup upgrade s'affiche via usePremiumGuard
    }

    const localId = `trip_${Date.now()}`;
    const trip = {
      id:              localId,
      name:            newTripData.name,
      destination:     newTripData.destination,
      destinationFlag: '✈️',
      style:           newTripData.style,                                                // compat ancien champ
      styles:          Array.isArray(newTripData.styles) && newTripData.styles.length   // nouveau : array
                         ? newTripData.styles
                         : (newTripData.style ? [newTripData.style] : ['city']),
      weather:         newTripData.weather,
      days:            parseInt(newTripData.days) || 5,
      departure:       newTripData.departure || '',
      returnDate:      newTripData.returnDate || '',
      estimatedTemp:   newTripData.estimatedTemp || null,
      packedItems:     [],
      travelLooks:     [],
    };

    // 1. Sauvegarder localement immédiatement
    const newList = [trip, ...trips];
    setTrips(newList);
    await persistTrips(newList);

    // 2. Rester sur la liste (permet à l'user de voir tous ses voyages)
    // Avant : setActiveTrip(trip) + setView('trip') → ouverture auto
    // Maintenant : retour direct à la liste, le user peut cliquer pour ouvrir
    setView('list');
    setNewTripData({ name:'', destination:'', style:'city', styles:['city'], weather:'temperate', days:5, departure:'', returnDate:'', destLat:null, destLon:null, estimatedTemp:null });

    // 3. Synchro DB en arrière-plan
    if (user?.accessToken) {
      try {
        const res = await fetch(`${API}/trips`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.accessToken}` },
          body: JSON.stringify({
            name:          newTripData.name,
            destination:   newTripData.destination,
            style:         newTripData.style,
            styles:        Array.isArray(newTripData.styles) && newTripData.styles.length
                            ? newTripData.styles
                            : (newTripData.style ? [newTripData.style] : ['city']),
            weather:       newTripData.weather,
            days:          parseInt(newTripData.days) || 5,
            departure:     newTripData.departure || null,
            returnDate:    newTripData.returnDate || null,
            destLat:       newTripData.destLat || null,
            destLon:       newTripData.destLon || null,
            estimatedTemp: newTripData.estimatedTemp || null,
          }),
        });
        if (res.ok) {
          const saved = await res.json();
          if (saved?.id && saved.id !== localId) {
            // Remplacer l'ID local par l'UUID DB
            setTrips(prev => {
              const updated = prev.map(tr => tr.id === localId ? { ...tr, id: saved.id } : tr);
              persistTrips(updated);
              return updated;
            });
            setActiveTrip(prev => prev?.id === localId ? { ...prev, id: saved.id } : prev);
          }
        }
      } catch(e) { console.warn('Trip DB sync failed (saved locally):', e.message); }
    }
  };
  const deleteTrip = async (trip) => {
    Alert.alert(
      t('travel_delete_trip_title'),
      t('travel_delete_trip_msg').replace('{name}', trip.name),
      [
        { text: t('cancel'), style: 'cancel' },
        { text: t('delete'), style: 'destructive', onPress: async () => {
          const newList = trips.filter(tr => tr.id !== trip.id);
          setTrips(newList);
          await persistTrips(newList);
          // Supprimer la valise voyage du stockage local
          try { await AsyncStorage.removeItem(`outfit_trip_packed_${trip.id}`); } catch(e) {}
          // Supprimer en DB
          if (user?.accessToken) {
            fetch(`${API}/trips/${trip.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }).catch(() => {});
          }
        }},
      ]
    );
  };
  if (view === 'new') return (
    <SafeAreaView style={s.safe}>
      <NewTripForm
        data={newTripData}
        onChange={setNewTripData}
        onSubmit={createTrip}
        onCancel={() => {
          setView('list');
          setEditingTripId(null);
          setNewTripData({ name:'', destination:'', style:'city', styles:['city'], weather:'temperate', days:5, departure:'', returnDate:'', destLat:null, destLon:null, estimatedTemp:null });
        }}
        isEditing={!!editingTripId}
        t={t}
      />
    </SafeAreaView>
  );

  if (view === 'trip' && activeTrip) return (
    <TripDetail
      trip={activeTrip}
      onBack={goBack}
      t={t}
      onPackedChange={(tripId, newPacked) => {
        setTrips(prev => prev.map(tr =>
          tr.id === tripId ? { ...tr, packedItems: newPacked } : tr
        ));
      }}
    />
  );

  // ═══ Écran de blocage si l'utilisateur n'a pas accès au Voyage ═══
  if (travelAccess === false) {
    return (
      <SafeAreaView style={s.safe}>
        <View style={[s.topbar, { paddingTop: topPadding + 8 }]}>
          <BackButton onPress={() => navigation.goBack()} />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={s.topbarBadge}>{t('travel_premium_badge')}</Text>
            <Text style={s.titlePremium}>{t('travel_premium_feature_title')}</Text>
          </View>
          <View style={{ width: 40 }} />
        </View>
        <Divider />

        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <View style={{ alignItems: 'center', maxWidth: 340 }}>
            <View style={{
              width: 90, height: 90, borderRadius: 45, backgroundColor: '#FFF9E6',
              borderWidth: 2, borderColor: '#F5DEB3',
              alignItems: 'center', justifyContent: 'center', marginBottom: 24,
            }}>
              <Text style={{ fontSize: 44 }}>✈️</Text>
            </View>
            <Text style={{ fontSize: 10, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 8 }}>
              {t('travel_premium_exclusive')}
            </Text>
            <Text style={{ fontSize: 22, fontWeight: '300', color: '#1A1A1A', letterSpacing: 0.3, textAlign: 'center', marginBottom: 14 }}>
              {t('travel_premium_hero_title')}
            </Text>
            <Text style={{ fontSize: 13, color: '#666', textAlign: 'center', lineHeight: 20, marginBottom: 28 }}>
              {t('travel_premium_hero_desc_p1')} <Text style={{ color: '#B8860B', fontWeight: '700' }}>Premium</Text>.
              {' '}{t('travel_premium_hero_desc_p2')}
            </Text>

            {/* Features list */}
            <View style={{ alignSelf: 'stretch', marginBottom: 28 }}>
              {[
                { icon: '🧳', text: t('travel_premium_feat_1') },
                { icon: '🌡️', text: t('travel_premium_feat_2') },
                { icon: '🛍️', text: t('travel_premium_feat_3') },
                { icon: '♻️', text: t('travel_premium_feat_4') },
              ].map((f, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8 }}>
                  <Text style={{ fontSize: 18, marginRight: 12 }}>{f.icon}</Text>
                  <Text style={{ flex: 1, fontSize: 13, color: '#333' }}>{f.text}</Text>
                </View>
              ))}
            </View>

            {/* CTA Premium → ouvre directement le popup d'abonnement */}
            <TouchableOpacity
              style={{
                backgroundColor: '#1A1A1A', paddingHorizontal: 32, paddingVertical: 16,
                borderRadius: 100, flexDirection: 'row', alignItems: 'center', gap: 10,
              }}
              activeOpacity={0.88}
              onPress={() => {
                // Force l'affichage du popup UpgradeModal sans passer par checkLimit
                // (l'user est déjà sur l'écran "Exclusif Premium" donc on sait qu'il
                // est non-premium et qu'il veut voir les options).
                setTravelPremiumTrigger('travel');
                setShowTravelPremium(true);
              }}
            >
              <Text style={{ color: '#D4AF72', fontSize: 13, fontWeight: '700', letterSpacing: 1.5 }}>
                {t('travel_premium_cta')}
              </Text>
              <Text style={{ color: '#D4AF72', fontSize: 16 }}>→</Text>
            </TouchableOpacity>
            <Text style={{ fontSize: 11, color: '#999', marginTop: 12 }}>
              {t('travel_premium_pricing')}
            </Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // Pendant le check (travelAccess === null), on ne rend rien pour éviter un flash
  if (travelAccess === null) {
    return (
      <SafeAreaView style={[s.safe, { alignItems: 'center', justifyContent: 'center' }]}>
        <ActivityIndicator size="small" color="#B8860B" />
      </SafeAreaView>
    );
  }

  return (
    <>
    <SafeAreaView style={s.safe}>

      <View style={[s.topbar, { paddingTop: topPadding + 8 }]}>
        <BackButton onPress={() => navigation.goBack()} />
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={s.topbarBadge}>{t('travel_topbar_badge')}</Text>
          <Text style={s.titlePremium}>{t('travel_wardrobe_title')}</Text>
        </View>
        <TouchableOpacity style={s.addBtnPremium} onPress={async () => {
          // Guard limite avant d'ouvrir le formulaire
          const canCreate = await travelGuard('trips', trips.length, false);
          if (canCreate) setView('new');
        }} activeOpacity={0.85}>
          <Text style={s.addBtnPremiumPlus}>＋</Text>
        </TouchableOpacity>
      </View>
      <Divider />

      <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
        {/* ═══ Hero bannière ═══ */}
        <View style={s.travelListHero}>
          <View style={s.travelListHeroIcon}>
            <Text style={{ fontSize: 36 }}>✈️</Text>
          </View>
          <View style={s.travelListHeroContent}>
            <Text style={s.travelListHeroTitle}>{t('travel_hero_title')}</Text>
            <Text style={s.travelListHeroSub}>
              {t('travel_hero_sub')}
            </Text>
          </View>
        </View>

        {/* ═══ Stats voyages ═══ */}
        {trips.length > 0 && (
          <View style={s.travelListStatsRow}>
            <View style={s.travelListStatCell}>
              <Text style={s.travelListStatNum}>{trips.length}</Text>
              <Text style={s.travelListStatLbl}>{t('travel_stats_trips')}</Text>
            </View>
            <View style={s.travelListStatDiv} />
            <View style={s.travelListStatCell}>
              <Text style={s.travelListStatNum}>
                {trips.reduce((s, t) => s + (t.packedItems?.length || 0), 0)}
              </Text>
              <Text style={s.travelListStatLbl}>{t('travel_stats_items')}</Text>
            </View>
            <View style={s.travelListStatDiv} />
            <View style={s.travelListStatCell}>
              <Text style={[s.travelListStatNum, { color: '#B8860B' }]}>
                ✨ {trips.filter(t => t.packedItems?.length >= 3).length}
              </Text>
              <Text style={s.travelListStatLbl}>{t('travel_stats_ready')}</Text>
            </View>
          </View>
        )}

        {/* ═══ Liste des voyages ═══ */}
        <View style={s.travelListSection}>
          <View style={s.travelListSectionHeader}>
            <Text style={s.travelListSectionIcon}>📍</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.travelListSectionTitle}>{t('my_trips')}</Text>
              <Text style={s.travelListSectionSub}>
                {trips.length === 0 ? t('travel_no_trips_yet') : t('travel_destinations_count').replace('{n}', trips.length).replace('{s}', trips.length > 1 ? 's' : '')}
              </Text>
            </View>
          </View>

          {/* État vide */}
          {trips.length === 0 && (
            <TouchableOpacity
              style={s.travelListEmpty}
              onPress={() => setView('new')}
              activeOpacity={0.85}
            >
              <Text style={s.travelListEmptyIcon}>🌍</Text>
              <Text style={s.travelListEmptyTitle}>{t('travel_first_trip_title')}</Text>
              <Text style={s.travelListEmptyDesc}>
                {t('travel_first_trip_desc')}
              </Text>
              <View style={s.travelListEmptyCta}>
                <Text style={s.travelListEmptyCtaText}>{t('travel_first_trip_cta')}</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Cards premium */}
          {trips.map(trip => {
            // Récupère tous les styles (nouveau champ) ou fallback sur style unique (ancien)
            const tripStyles = Array.isArray(trip.styles) && trip.styles.length > 0
              ? trip.styles
              : (trip.style ? [trip.style] : []);
            const stylesInfo = tripStyles
              .map(sid => TRIP_STYLES.find(st => st.id === sid))
              .filter(Boolean);
            const primaryStyle = stylesInfo[0]; // pour getStyleColor
            const weatherInfo = WEATHER_PROFILES[trip.weather];
            const isReady = (trip.packedItems?.length || 0) >= 3;
            return (
              <View key={trip.id} style={s.tripCardPremium}>
                <TouchableOpacity
                  style={s.tripCardPremiumMain}
                  onPress={() => openTrip(trip)}
                  activeOpacity={0.85}
                >
                  {/* Colonne gauche : flag + style */}
                  <View style={s.tripCardPremiumLeft}>
                    <Text style={s.tripCardPremiumFlag}>{trip.destinationFlag || '🌍'}</Text>
                    <View style={[s.tripCardPremiumStyleDot, { backgroundColor: getStyleColor(primaryStyle?.id || trip.style) }]} />
                  </View>

                  {/* Colonne centre : info */}
                  <View style={s.tripCardPremiumInfo}>
                    <View style={s.tripCardPremiumTitleRow}>
                      <Text style={s.tripCardPremiumName} numberOfLines={1}>{trip.name}</Text>
                      {isReady && (
                        <View style={s.tripCardPremiumReadyBadge}>
                          <Text style={s.tripCardPremiumReadyText}>✓</Text>
                        </View>
                      )}
                    </View>
                    <Text style={s.tripCardPremiumDest}>📍 {trip.destination}</Text>
                    {trip.departure && (
                      <Text style={s.tripCardPremiumDates}>
                        {formatTripDateRange(trip.departure, trip.returnDate)}
                      </Text>
                    )}
                    <View style={s.tripCardPremiumMeta}>
                      {/* Afficher jusqu'à 3 styles (les autres sous un + X) */}
                      {stylesInfo.slice(0, 3).map(si => (
                        <View key={si.id} style={s.tripCardPremiumPill}>
                          <Text style={s.tripCardPremiumPillIcon}>{si.icon}</Text>
                          <Text style={s.tripCardPremiumPillText}>{si.labelKey ? t(si.labelKey) : si.label}</Text>
                        </View>
                      ))}
                      {stylesInfo.length > 3 && (
                        <View style={s.tripCardPremiumPill}>
                          <Text style={s.tripCardPremiumPillText}>+{stylesInfo.length - 3}</Text>
                        </View>
                      )}
                      <View style={s.tripCardPremiumPill}>
                        <Text style={s.tripCardPremiumPillIcon}>{weatherInfo?.icon || '🌤'}</Text>
                        <Text style={s.tripCardPremiumPillText}>{trip.days} {t('days_count')}</Text>
                      </View>
                      {(trip.tempMin != null && trip.tempMax != null) ? (
                        <View style={[s.tripCardPremiumPill, { backgroundColor: '#FFF9E6', borderColor: '#F5DEB3' }]}>
                          <Text style={[s.tripCardPremiumPillText, { color: '#B8860B', fontWeight: '700' }]}>
                            {trip.tempMin}–{trip.tempMax}°C
                          </Text>
                        </View>
                      ) : trip.estimatedTemp != null ? (
                        <View style={[s.tripCardPremiumPill, { backgroundColor: '#FFF9E6', borderColor: '#F5DEB3' }]}>
                          <Text style={[s.tripCardPremiumPillText, { color: '#B8860B', fontWeight: '700' }]}>
                            {trip.estimatedTemp}°C
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </View>

                  {/* Colonne droite : compteur */}
                  <View style={s.tripCardPremiumRight}>
                    <Text style={s.tripCardPremiumCount}>{trip.packedItems?.length || 0}</Text>
                    <Text style={s.tripCardPremiumCountLbl}>{t('travel_pieces_short')}</Text>
                    <Text style={s.tripCardPremiumArrow}>›</Text>
                  </View>
                </TouchableOpacity>

                {/* Actions edit + delete */}
                <View style={s.tripCardPremiumActions}>
                  <TouchableOpacity
                    style={s.tripCardPremiumEdit}
                    onPress={() => startEditTrip(trip)}
                    activeOpacity={0.7}
                  >
                    <Text style={s.tripCardPremiumEditText}>✎</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={s.tripCardPremiumDel}
                    onPress={() => deleteTrip(trip)}
                    activeOpacity={0.7}
                  >
                    <Text style={s.tripCardPremiumDelText}>🗑</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}

          {/* Bouton "Nouveau voyage" premium — visible si au moins 1 voyage existe */}
          {trips.length > 0 && (
            <TouchableOpacity style={s.newTripCardPremium} onPress={async () => {
              // ═══ Guard PRE-formulaire ═══
              // On vérifiez la limite AVANT d'ouvrir le formulaire pour éviter que
              // l'utilisateur Basic remplisse tous les champs (nom, destination, dates...)
              // pour finalement se prendre le popup. UpgradeModal s'affiche directement.
              const canCreate = await travelGuard('trips', trips.length, false);
              if (canCreate) setView('new');
            }} activeOpacity={0.85}>
              <Text style={s.newTripCardPremiumIcon}>＋</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.newTripCardPremiumTitle}>{t('new_trip')}</Text>
                <Text style={s.newTripCardPremiumSub}>{t('travel_new_trip_card_sub')}</Text>
              </View>
              <Text style={s.newTripCardPremiumArrow}>→</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
      <UpgradeModal
        visible={showTravelPremium}
        onClose={closeTravelPremium}
        currentTier={limitInfo?.tier || user?.tier || user?.plan || 'free'}
        feature={limitInfo?.trigger || 'travel'}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit } : null}
        onSubscribe={async (planId) => {
          const { activateSubscription } = require('../services/TestSubscribeService');
          await activateSubscription(planId, user, null);
        }}
      />
    </>
  );
}

/* ══ Détail d'un voyage ═════════════════════════════════════ */
function TripDetail({ trip, onBack, t, onPackedChange }) {
  const { user, getValidToken } = useAuth();
  const { showPremium: showTravelPremium, limitInfo, guardCheck: travelGuard, closePremium: closeTravelPremium } = usePremiumGuard(user);
  // ═══ FIX 2026-04 : styles et theme manquants ═══
  // Avant : TripDetail référençait s.topbar / s.title / s.subtitle / s.safe
  //   mais ne définissait JAMAIS la variable `s`. Ça fonctionnait peut-être
  //   par accident grâce à du hoisting JS, mais avec les ajouts récents
  //   (BackButton + topPadding) le crash s'est révélé :
  //   "ReferenceError: Property 's' doesn't exist".
  // Maintenant : on crée s exactement comme le composant parent et NewTripForm.
  const { dark, theme } = useTheme();
  const s = useMemo(() => makeStyles(theme, dark), [theme, dark]);
  // ═══ FIX 2026-04 : safe-area Android (cf. composant parent) ═══
  const insets = useSafeAreaInsets();
  const topPadding = Platform.OS === 'android'
    ? Math.max(insets.top, StatusBar.currentHeight || 24)
    : Math.max(insets.top, 0);
  const STORAGE_KEY = `outfit_trip_packed_${trip.id}`;
  const [tab,           setTab]           = useState('bag');
  const [packed,        setPacked]        = useState(trip.packedItems || []);
  const [wardrobe,      setWardrobe]      = useState([]);
  const [catalog,       setCatalog]       = useState([]);
  const [loadingCat,    setLoadingCat]    = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);
  const [showWardrobe,  setShowWardrobe]  = useState(false);
  // ═══ Affichage progressif par catégorie : 4 au début, +4 par clic ═══
  const [visibleByCat,  setVisibleByCat]  = useState({}); // { Hauts: 4, Bas: 4, ... }
  // Limite valise = limite dressing du tier (free=10, basic=30, premium=100, gold=∞)
  const [maxPacked,     setMaxPacked]     = useState(15);
  useEffect(() => {
    (async () => {
      try {
        const { getLimits } = require('../services/PremiumService');
        const limits = await getLimits(user);
        const m = limits?.wardrobe_items;
        setMaxPacked(m === Infinity || !isFinite(m) ? 999 : m);
      } catch { setMaxPacked(15); }
    })();
  }, [user?.id, user?.tier, user?.plan]);

  // Nombre max de looks voyage selon le tier (exactement looks_per_day comme Looks)
  // Free=3, Basic=10, Premium/Gold=∞ (utilise 200 comme cap pratique générateur)
  const [maxTravelLooks, setMaxTravelLooks] = useState(5);
  const [isTravelUnlimited, setIsTravelUnlimited] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { getLimits } = require('../services/PremiumService');
        const limits = await getLimits(user);
        const m = limits?.looks_per_day;
        const unlimited = (m === Infinity || !isFinite(m));
        setIsTravelUnlimited(unlimited);
        // Premium/Gold : 200 pour que le générateur ne bride pas
        // Free/Basic : limite du tier
        setMaxTravelLooks(unlimited ? 200 : m);
      } catch { setMaxTravelLooks(5); setIsTravelUnlimited(false); }
    })();
  }, [user?.id, user?.tier, user?.plan]);
  // v1.12.135 — currentWeather inclut tempMin/tempMax pour afficher la plage sur la duree du sejour
  const [currentWeather, setCurrentWeather] = useState({
    key: trip.weather || 'temperate',
    temp: trip.estimatedTemp || null,
    tempMin: trip.tempMin != null ? trip.tempMin : null,
    tempMax: trip.tempMax != null ? trip.tempMax : null,
  });
  const [selectedItems, setSelectedItems] = useState(new Set());
  // v1.12.142 — Recherche + filtres categorie dans la modal dressing voyage
  const [wardrobeSearch, setWardrobeSearch] = useState('');
  const [wardrobeFilterCat, setWardrobeFilterCat] = useState('Tout');
  const [selectedProduct, setSelectedProduct] = useState(null);
  // v1.12.140 — clic sur article du dressing (Looks voyage) ouvre la fiche detail
  const [selectedWardrobeItem, setSelectedWardrobeItem] = useState(null);
  const [boughtProduct,   setBoughtProduct]   = useState(null);

  // ═══ v1.10.26 : States pour le Try-on virtuel sur les looks voyage ═══
  const [tryOnOutfit, setTryOnOutfit] = useState(null);
  const [tryOnAuthToken, setTryOnAuthToken] = useState(null);

  const openTryOnVertex = useCallback(async (outfit) => {
    if (!outfit?.items?.length) return;
    if (!user) {
      Alert.alert(t('travel_connection_required'), t('travel_connection_required_msg'));
      return;
    }
    // v1.12.620 — Plus de shortcut "tier === free reject" : Free avec bonus
    // doit pouvoir ouvrir. Le pre-check fetchTryOnUsage utilise la limit
    // backend qui inclut maintenant le bonus addons.
    try {
      const token = getValidToken ? await getValidToken() : null;
      // v1.12.491 — Pre-check quota AVANT ouverture modal (évite crash SIGABRT iOS)
      if (token) {
        try {
          const { fetchTryOnUsage } = require('../services/TryOnService');
          const u = await fetchTryOnUsage(token);
          if (u && (u.limit === 0 || u.count >= u.limit)) {
            DeviceEventEmitter.emit('tryon_limit_reached', {
              tier: u.tier, limit: u.limit, current: u.count,
              isPaidTier: u.isPaidTier, nextResetDate: u.nextResetDate,
            });
            return;
          }
        } catch (e) { console.warn('[Travel] tryon quota check failed:', e?.message); }
      }
      setTryOnAuthToken(token);
      setTryOnOutfit(outfit);
    } catch (e) {
      console.warn('[Travel] openTryOnVertex auth failed:', e?.message);
    }
  }, [getValidToken, user]);

  // v1.12.121 — flow simplifie : "Try-on" ouvre directement le mannequin.
  const handleTryOn = useCallback((outfit) => {
    if (!outfit?.items?.length) return;
    openTryOnVertex(outfit);
  }, [openTryOnVertex]);

  // ═══ CHECKLIST À NE PAS OUBLIER ═══
  // Liste cochée persistée par trip (chaque item coché = "j'ai pris")
  const CHECKLIST_KEY = `outfit_travel_checklist_${trip?.id || 'default'}`;
  const [checkedItems, setCheckedItems] = useState({}); // { 'Passeport / CNI': true, ... }
  useEffect(() => {
    AsyncStorage.getItem(CHECKLIST_KEY).then(raw => {
      if (raw) { try { setCheckedItems(JSON.parse(raw)); } catch {} }
    }).catch(() => {});
  }, [CHECKLIST_KEY]);
  const toggleCheck = useCallback((key) => {
    setCheckedItems(prev => {
      const next = { ...prev, [key]: !prev[key] };
      AsyncStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });
  }, [CHECKLIST_KEY]);

  // ═══ BONS PLANS IA (appel Claude) ═══
  // v1.12.119 — cooldown 1h entre 2 générations pour limiter les coûts IA
  const AI_TIPS_COOLDOWN_MS = 60 * 60 * 1000; // 1 heure
  const [aiTips,       setAiTips]       = useState([]);
  const [aiTipsLoading,setAiTipsLoading]= useState(false);
  const [aiTipsError,  setAiTipsError]  = useState(null);
  const [aiTipsLastFetch, setAiTipsLastFetch] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now()); // re-render pour countdown
  const AI_TIPS_KEY = `outfit_travel_aitips_${trip?.id || 'default'}`;
  // Charger cache AI tips
  useEffect(() => {
    AsyncStorage.getItem(AI_TIPS_KEY).then(raw => {
      if (!raw) return;
      try {
        const { tips, ts } = JSON.parse(raw);
        if (typeof ts === 'number') setAiTipsLastFetch(ts);
        // Cache 7 jours
        if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000 && Array.isArray(tips)) {
          setAiTips(tips);
        }
      } catch {}
    }).catch(() => {});
  }, [AI_TIPS_KEY]);

  // Tick toutes les 30s pour rafraichir le countdown du cooldown
  useEffect(() => {
    const elapsed = Date.now() - aiTipsLastFetch;
    if (elapsed >= AI_TIPS_COOLDOWN_MS) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [aiTipsLastFetch]);

  const aiTipsCooldownLeft = Math.max(0, AI_TIPS_COOLDOWN_MS - (nowTick - aiTipsLastFetch));
  const aiTipsOnCooldown = aiTipsLastFetch > 0 && aiTipsCooldownLeft > 0;

  const fetchAITips = useCallback(async () => {
    // v1.12.119 — cooldown 1h entre 2 générations
    const elapsed = Date.now() - aiTipsLastFetch;
    if (aiTipsLastFetch > 0 && elapsed < AI_TIPS_COOLDOWN_MS) {
      const min = Math.ceil((AI_TIPS_COOLDOWN_MS - elapsed) / 60000);
      setAiTipsError(t('travel_ai_cooldown').replace('{min}', min).replace('{s}', min > 1 ? 's' : ''));
      return;
    }
    setAiTipsLoading(true);
    setAiTipsError(null);
    try {
      // v1.12.305 — supprimé : les tips IA voyage ne comptent PLUS dans le quota
      // (le user a demandé que seuls les ajouts au dressing comptent).

      const styleLabel = TRIP_STYLES.find(s => s.id === trip.style)?.label || trip.style;
      const prompt = `Tu es un expert voyage. Je pars en voyage type "${styleLabel}" à ${trip.destination || 'destination non précisée'} pour ${trip.days} jours avec une météo ${trip.weather || 'tempérée'}.

Donne-moi 6 astuces pratiques courtes (1 phrase max chacune) pour ce voyage :
- 2 astuces sur quoi emporter (intelligemment, éviter le superflu)
- 2 astuces sur place (restaurants, transports, quartiers, culture locale si destination connue)
- 2 astuces budget/bon plan (carte de transport, apps utiles, réductions, timing)

Réponds UNIQUEMENT en JSON:
{
  "tips": [
    {"icon": "🎒", "category": "valise", "text": "..."},
    {"icon": "🗺️", "category": "place", "text": "..."},
    {"icon": "💰", "category": "bon plan", "text": "..."}
  ]
}`;

      // ═══ Récupérer un token valide (refresh auto si expiré) ═══
      let token = null;
      try {
        token = typeof getValidToken === 'function' ? await getValidToken() : null;
      } catch (e) { console.warn('[TravelAI] getValidToken:', e.message); }
      // Fallback sur user.accessToken si getValidToken ne donne rien
      if (!token) token = user?.accessToken || null;
      if (!token) {
        setAiTipsError(t('travel_ai_not_connected'));
        setAiTipsLoading(false);
        return;
      }

      // Appel via Lambda (endpoint existant /ai/trending-products avec action=travel-tips)
      // Évite de déclarer une nouvelle route dans API Gateway
      const { AWS_CONFIG } = require('../config/aws_config');
      const res = await fetch(`${AWS_CONFIG.apiUrl}/ai/trending-products`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action:      'travel-tips',
          style:       trip.style,
          destination: trip.destination,
          days:        trip.days,
          weather:     trip.weather,
          prompt,
        }),
      });

      if (res.status === 401) {
        setAiTipsError(t('travel_ai_session_expired'));
        setAiTipsLoading(false);
        return;
      }

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${errBody ? ': ' + errBody.slice(0, 80) : ''}`);
      }

      const data = await res.json();
      const tips = Array.isArray(data.tips) ? data.tips : [];
      if (tips.length === 0) throw new Error(t('travel_ai_no_tips'));

      setAiTips(tips);
      const now = Date.now();
      setAiTipsLastFetch(now);
      setNowTick(now);
      await AsyncStorage.setItem(AI_TIPS_KEY, JSON.stringify({ tips, ts: now })).catch(() => {});

      // v1.12.305 — supprimé : les tips IA voyage ne comptent PLUS dans le quota
      // (seul "ajout au dressing" compte côté user).
    } catch (e) {
      console.warn('[TravelAI] fetchAITips:', e.message);
      setAiTipsError(t('travel_ai_error'));
    } finally {
      setAiTipsLoading(false);
    }
  }, [trip, user, AI_TIPS_KEY, getValidToken, aiTipsLastFetch]);

  // Charger packed depuis AsyncStorage
  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY).then(raw => {
      if (raw) { try { setPacked(JSON.parse(raw)); } catch(e) {} }
    }).catch(() => {});
  }, [STORAGE_KEY]);

  // Sauvegarder packed à chaque modification
  useEffect(() => {
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(packed)).catch(() => {});
  }, [packed, STORAGE_KEY]);

  // ── Mettre à jour la météo à l'ouverture du voyage ──
  useEffect(() => {
    if (!trip.destLat || !trip.destLon || !trip.departure) return;
    const today     = new Date();
    const tripDate  = new Date(trip.departure);
    const diffDays  = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
    // Recalculer seulement si voyage dans les 16 prochains jours (prévisions disponibles)
    // ou si pas encore de temp enregistrée. v1.12.135 : aussi si on n'a pas de tempMin/tempMax.
    const hasRange = trip.tempMin != null && trip.tempMax != null;
    if (!trip.estimatedTemp || !hasRange || (diffDays >= 0 && diffDays <= 16)) {
      refreshWeather(trip.destLat, trip.destLon, trip.departure);
    }
  }, [trip.id]);

  const refreshWeather = useCallback(async (lat, lon, dateStr) => {
    if (!lat || !lon || !dateStr) return;
    try {
      const today    = new Date();
      const tripDate = new Date(dateStr);
      const diffDays = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
      let tempC    = null;
      let tempMin  = null;
      let tempMax  = null;
      let weatherKey = trip.weather || 'temperate';
      // v1.12.135 — Utilise la plage complete du sejour pour avoir min/max
      const endStr = trip.returnDate || dateStr;

      if (diffDays >= 0 && diffDays <= 16) {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum` +
          `&timezone=auto&start_date=${dateStr}&end_date=${endStr}`
        );
        if (res.ok) {
          const json   = await res.json();
          const tmaxArr = json.daily?.temperature_2m_max || [];
          const tminArr = json.daily?.temperature_2m_min || [];
          const precipArr = json.daily?.precipitation_sum || [];
          if (tmaxArr.length > 0 && tminArr.length > 0) {
            tempMax = Math.round(Math.max(...tmaxArr.filter(v => v != null)));
            tempMin = Math.round(Math.min(...tminArr.filter(v => v != null)));
            tempC   = Math.round((tempMax + tempMin) / 2);
            const totalPrecip = precipArr.reduce((sum, p) => sum + (p || 0), 0);
            if      (totalPrecip > 5 * tmaxArr.length) weatherKey = 'rainy';
            else if (tempC >= 30) weatherKey = 'hot_dry';
            else if (tempC >= 25) weatherKey = 'hot_humid';
            else if (tempC >= 15) weatherKey = 'temperate';
            else if (tempC >= 5)  weatherKey = 'cold';
            else                  weatherKey = 'very_cold';
          }
        }
      } else if (diffDays > 16) {
        const res = await fetch(`https://wttr.in/${lat},${lon}?format=j1`);
        if (res.ok) {
          const json = await res.json();
          const avg  = json?.weather?.[0];
          if (avg) {
            tempMax = parseInt(avg.maxtempC);
            tempMin = parseInt(avg.mintempC);
            tempC = Math.round((tempMax + tempMin) / 2);
            weatherKey = tempC >= 30 ? 'hot_dry' : tempC >= 25 ? 'hot_humid' :
                         tempC >= 15 ? 'temperate' : tempC >= 5 ? 'cold' : 'very_cold';
          }
        }
      }

      if (tempC != null) {
        setCurrentWeather({ key: weatherKey, temp: tempC, tempMin, tempMax });
        // Sauvegarder en DB
        if (user?.accessToken && !trip.id.startsWith('trip_')) {
          fetch(`${API}/trips/${trip.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.accessToken}` },
            body: JSON.stringify({ weather: weatherKey, estimatedTemp: tempC, tempMin, tempMax }),
          }).catch(() => {});
        }
      }
    } catch(e) {}
  }, [trip, user]);

  // Charger dressing
  // État bandeau upgrade dressing voyage (si dépasse tier)
  const [totalTravelWardrobeSize, setTotalTravelWardrobeSize] = useState(0);
  const [showTravelUpgradeBanner, setShowTravelUpgradeBanner] = useState(false);

  const loadWardrobe = useCallback(async () => {
    if (!user?.accessToken) return;
    try {
      const d = await getWardrobe(user.id, user.accessToken);
      const fullArr = Array.isArray(d) ? d : [];
      setTotalTravelWardrobeSize(fullArr.length);

      // ═══ LIMITATION PAR TIER (identique au dressing principal) ═══
      // Si le dressing dépasse la limite d'abonnement, on n'utilise que les N premiers
      // v1.12.142 — Premium/Gold (illimite) : jamais de banniere upgrade.
      let limited = fullArr;
      if (!isTravelUnlimited && maxPacked && isFinite(maxPacked) && fullArr.length > maxPacked) {
        limited = fullArr.slice(0, maxPacked);
        setShowTravelUpgradeBanner(true);
        console.log(`[Travel] wardrobe limited: ${limited.length}/${fullArr.length} (tier=${user?.tier})`);
      } else {
        setShowTravelUpgradeBanner(false);
      }

      // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier+bonus)
      // pour qu'ils n'apparaissent pas dans la valise voyage
      setWardrobe(limited.filter(it => !it._hidden));
    } catch(e) {}
  }, [user, maxPacked, isTravelUnlimited]);

  useEffect(() => { loadWardrobe(); }, [loadWardrobe]);

  // Recharger dressing après achat confirmé
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_updated', loadWardrobe);
    return () => sub.remove();
  }, [loadWardrobe]);

  // Charger catalogue adapté au style voyage
  // v1.12.906 — Source unifiée : /catalog API (partner_products en DB),
  // remplace Zalando/ASOS/H&M/DynamicCatalog (legacy, partenaires inactifs).
  // Renvoie uniquement les partenaires actifs aujourd'hui :
  // iQueens, TOUS, SUIT, ANN, COUTR, Tamaris, drestige, Etsy (selon DB).
  const loadCatalog = useCallback(async () => {
    setLoadingCat(true);
    try {
      const gender = user?.gender || 'F';

      // v1.12.908 — Fetch équitable PAR PARTENAIRE (au lieu d'un seul /catalog
      // qui trie par created_at DESC et retourne 1971/2000 Tamaris à cause de
      // l'ingestion récente). On force la diversité avec 300 items/partenaire.
      const PARTNERS_TO_FETCH = [
        'iqueens', 'tous', 'suit_negozi', 'ann_demeulemeester',
        'coutr', 'tamaris', 'drestige', 'etsy',
      ];
      const partnerResults = await Promise.allSettled(
        PARTNERS_TO_FETCH.map(p => getCatalog({
          partner: p,
          limit: 300,
          available: true,
          gender,
        }).catch(() => []))
      );
      const items = partnerResults.flatMap(r => (r.status === 'fulfilled' ? (r.value || []) : []));
      // Stats par partenaire (debug)
      const partnerCounts = {};
      items.forEach(it => { partnerCounts[it.partner] = (partnerCounts[it.partner] || 0) + 1; });
      console.log('[TRAVEL] partners distribution:', partnerCounts);
      const all = (items || []).filter(p => {
        if (!p) return false;
        const img = p.image || p.image_url;
        if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
        const link = p.affiliateUrl || p.affiliate_url || p.originalUrl || p.url;
        if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
        const priceNum = parseFloat(p.price);
        if (isNaN(priceNum) || priceNum <= 0) return false;
        return true;
      });
      console.log(`[TRAVEL] catalog loaded from /catalog API: ${all.length} items (current partners only)`);

      const withAff = applySkimlinksToProducts(all, user?.id || '');

      // ═══ MERGE doublons : agrège les tailles iQueens en un seul produit ═══
      // Cas concret : iQueens scrape "Adidas T-Shirt Femme XL" et "Adidas
      // T-Shirt Femme S" comme 2 produits distincts. mergeDuplicateProducts()
      // les fusionne en 1 produit avec sizes=['S','XL'] visible dans la fiche.
      let deduped = mergeDuplicateProducts(withAff);
      if (deduped.length !== withAff.length) {
        console.log(`[TRAVEL] merged ${withAff.length - deduped.length} doublons (sizes agrégées)`);
      }

      // ═══ RECLASSIFICATION CATÉGORIE via nom (corrige erreurs partners) ═══
      try {
        const { normalizeCategoriesBatch } = require('../services/CategoryNormalizer');
        deduped = normalizeCategoriesBatch(deduped);
        const corrected = deduped.filter(p => p._originalCategory).length;
        if (corrected > 0) console.log(`[TRAVEL] catégorie corrigée pour ${corrected} articles`);
      } catch(e) {}

      // ═══ FILTRE GENRE STRICT (Hauts agressif) ═══
      try {
        const GF = require('../services/GenderFilter');
        if (GF && GF.filterHautsByGenderStrict) {
          const before = deduped.length;
          deduped = GF.filterHautsByGenderStrict(deduped, gender);
          if (deduped.length !== before) {
            console.log(`[TRAVEL] gender filter (Hauts strict): ${deduped.length}/${before}`);
          }
        }
        // ═══ FILTRE GENRE GLOBAL (toutes catégories) ═══
        // Pour user M : rejette les robes, jupes, articles explicitement féminins
        // Pour user F : rejette les articles explicitement masculins
        if (GF && GF.filterByGender && gender && ['M', 'F'].includes(gender)) {
          const before = deduped.length;
          deduped = GF.filterByGender(deduped, gender);
          if (deduped.length !== before) {
            console.log(`[TRAVEL] gender filter (all cats): ${deduped.length}/${before} (target=${gender})`);
          }
        }
        // ═══ NETTOYAGE CATÉGORIES MAL ATTRIBUÉES ═══
        // Rejette les produits dont le nom contredit la catégorie (ex: "jupe" dans Robes)
        if (GF && GF.rejectMiscategorized) {
          const before = deduped.length;
          deduped = GF.rejectMiscategorized(deduped);
          if (deduped.length !== before) {
            console.log(`[TRAVEL] miscategorized rejected: ${deduped.length}/${before}`);
          }
        }
      } catch(e) {}

      // ═══ ENRICHISSEMENT TENDANCES IA ═══
      try {
        const { getTrendingProducts } = require('../services/TrendingService');
        const { products: enriched, trendingCount } = await getTrendingProducts(deduped, gender);
        console.log(`[TRAVEL] ${trendingCount} produits tendance détectés`);
        deduped = enriched;
      } catch(e) {
        console.warn('[TRAVEL] trending failed:', e.message);
      }

      setCatalog(deduped);
    } catch(e) { console.warn('[TRAVEL] loadCatalog:', e.message); }
    setLoadingCat(false);
  }, [user, trip.style]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Pull-to-refresh
  const [regenCounter, setRegenCounter] = useState(0);
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadWardrobe(), loadCatalog()]);
    setRegenCounter(c => c + 1); // force regen des looks
    setRefreshing(false);
  }, [loadWardrobe, loadCatalog]);

  // Looks recalculés automatiquement quand packed change
  const [generatedLooks, setGeneratedLooks] = React.useState([]);

  // v1.12.119 — Auto-trigger regen quand on entre dans l'onglet Looks
  // si la valise a >=3 articles mais qu'aucun look n'a encore été généré
  // (cache vide et useEffect principal pas encore tiré).
  React.useEffect(() => {
    if (tab !== 'looks') return;
    if (packed.length < 3) return;
    if (generatedLooks.length > 0) return;
    setRegenCounter(c => c + 1);
  }, [tab, packed.length, generatedLooks.length]);

  // Filtre d'occasion pour Travel looks (casual/business/elegant/sport/...)
  const [travelOccasion, setTravelOccasion] = React.useState('Tout');
  // Si la sélection actuelle n'a aucune tenue (e.g. après nouveau set), reset 'Tout'
  React.useEffect(() => {
    if (travelOccasion === 'Tout') return;
    const hasMatch = generatedLooks.some(l => {
      const tag = (l.bestTag || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
      return tag === travelOccasion;
    });
    if (!hasMatch) setTravelOccasion('Tout');
  }, [generatedLooks, travelOccasion]);
  // v1.12.102 — Banks pour OutfitEngine (mêmes paramètres que Looks)
  const [colorBank, setColorBank] = React.useState(null);
  const [materialBank, setMaterialBank] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const cb = await getColorBank(); if (!cancelled) setColorBank(cb); } catch {}
      try { const mb = await getMaterialBank(); if (!cancelled) setMaterialBank(mb); } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Chargement plus d'articles pour un look voyage spécifique
  const [loadingMoreTravelFor, setLoadingMoreTravelFor] = React.useState(null);
  const handleLoadMoreTravelLook = React.useCallback(async (outfit) => {
    if (!outfit) return;
    const lookId = outfit.id;
    if (loadingMoreTravelFor === lookId) return;
    setLoadingMoreTravelFor(lookId);
    try {
      // v1.12.906 — Source unifiée /catalog (Zalando/ASOS/H&M/Zara retirés)
      // v1.12.907 — limit 6000→2000 (sinon 502 Lambda response payload exceeds 6MB)
      const g = user?.gender || 'F';
      const extra = await getCatalog({
        limit: 2000,
        available: true,
        gender: g,
      }).catch(() => []);
      const cleaned = (extra || []).filter(p =>
        p && (p.image || p.image_url) && (p.affiliateUrl || p.affiliate_url || p.url) && (p.price || 0) > 0
      );

      const seen = new Set((catalog || []).map(p => p.id));
      let newOnes = cleaned.filter(p => p.id && !seen.has(p.id));

      // ═══ FILTRE GENRE STRICT ═══
      try {
        const GF = require('../services/GenderFilter');
        if (GF && GF.filterHautsByGenderStrict) {
          newOnes = GF.filterHautsByGenderStrict(newOnes, g);
        }
      } catch(e) {}

      if (newOnes.length === 0) return;
      console.log(`[Travel] loadMore: +${newOnes.length} nouveaux articles`);

      const newCatalog = [...(catalog || []), ...newOnes];
      setCatalog(newCatalog);

      // Re-suggérer pour ce look avec catalog étendu
      const allCatalog = newCatalog.filter(p => p && p.image && p.price > 0 && (p.originalUrl || p.affiliateUrl || p.url));
      const shuffled = [...allCatalog];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      const tripTempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;
      const STYLE_MAP = { soiree: 'Soirée', casual: 'Casual', business: 'Bureau', sport: 'Sport', plage: 'Casual', city: 'Casual' };
      const occasion = STYLE_MAP[outfit.occasion] || STYLE_MAP[trip.style] || 'Casual';
      // Helper partagé : détecte manquants + propose remplacements pour chaque item
      let flatSugg = getOutfitCatalogSuggestions(
        { items: outfit.items },
        shuffled,
        {
          tempC: tripTempC,
          occasion,
          gender: user?.gender || 'F',
          maxPicks: 10,
          perCategory: 3,
          alreadySuggestedIds: new Set(),
          includeReplace: true,
        }
      );
      if (flatSugg.length < 6) {
        const legacy = suggestCatalogItems({ items: outfit.items }, shuffled, 8);
        flatSugg = flatSugg.concat(legacy.filter(x => !flatSugg.some(s => s.id === x.id)));
      }

      // Merge avec suggestions existantes
      const existingIds = new Set((outfit.suggestions || []).map(s => s.id));
      const merged = [
        ...(outfit.suggestions || []),
        ...flatSugg.filter(s => !existingIds.has(s.id)),
      ].slice(0, 12);

      setGeneratedLooks(prev => prev.map((l, idx) => {
        const id = `tlook_${idx}_${l.id || idx}`;
        return id === lookId ? { ...l, catalogSuggestions: merged } : l;
      }));
    } catch(e) {
      console.warn('[Travel] loadMore error:', e.message);
    } finally {
      setLoadingMoreTravelFor(null);
    }
  }, [catalog, loadingMoreTravelFor, trip?.estimatedTemp, trip?.style, user?.gender]);

  React.useEffect(() => {
    if (!packed.length) { setGeneratedLooks([]); return; }

    // ═══ CACHE CHECK : skip recompute si mêmes inputs ET pas de regen forcé ═══
    // regenCounter > 0 = user a cliqué "Nouvelles variations" → bypass cache
    if (regenCounter === 0) {
      try {
        const { makeKey, getCache } = require('../services/LooksCacheService');
        const tempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;
        const cacheKey = makeKey('travel', packed, catalog, tempC, trip.style, maxTravelLooks);
        const cached = getCache(cacheKey);
        if (cached) {
          console.log(`[TRAVEL] ✓ cache hit (${cached.length} looks)`);
          setGeneratedLooks(cached);
          return;
        }
      } catch {}
    }

    // ═══ Déférer le calcul lourd pour éviter le freeze UI ═══
    // Avec 5000+ articles catalogue × N looks × findCatalogSuggestions,
    // le calcul bloque le thread principal. setTimeout(0) laisse React
    // flusher avant de lancer le calcul.
    const timer = setTimeout(() => {
      // v1.12.102 — Algorithme Looks (OutfitEngine) appliqué à la valise.
      // Avant : generateTravelLooks (algo custom basique scoring couleur).
      // Maintenant : OutfitEngine.generateOutfits avec wardrobe = packed (valise).
      // Avantages : cohésion couleur+matière+style+formalité (mêmes critères
      // que Looks), tagMatches multi-occasions, dédup CORE.
      const tempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;
      // Mapping trip.style → targetStyle/targetOccasion OutfitEngine
      const STYLE_TO_TARGET = {
        soiree:   { style: 'chic',     occasion: 'soirée' },
        casual:   { style: 'casual',   occasion: 'casual' },
        business: { style: 'workwear', occasion: 'bureau' },
        sport:    { style: 'sport',    occasion: 'sport' },
        plage:    { style: 'casual',   occasion: 'plage' },
        city:     { style: 'casual',   occasion: 'casual' },
      };
      const tgt = STYLE_TO_TARGET[trip.style] || { style: null, occasion: null };

      // Tags candidats : styleTags des items valise + occasion cible
      const seedTags = new Set();
      for (const it of packed) {
        const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
        for (const tag of tags) if (tag) seedTags.add(String(tag).toLowerCase());
      }
      if (tgt.occasion) seedTags.add(tgt.occasion);
      const candidateTags = Array.from(seedTags).slice(0, 30);

      const generatedRaw = OutfitEngineGenerate({
        wardrobe: packed,
        tempC,
        maxOutfits: 100,
        candidateTags,
        guide: null,
        targetStyle: tgt.style,
        targetOccasion: tgt.occasion,
        colorBank,
        materialBank,
      });

      // Mapping bestTag → occasion key utilisé par le filtre UI (casual/work/evening/sport/beach)
      const TAG_TO_OCC = {
        casual: 'casual', basic: 'casual', everyday: 'casual',
        polyvalent: 'casual', daily: 'casual', streetwear: 'casual',
        minimaliste: 'casual', urbain: 'casual',
        chic: 'evening', élégant: 'evening', elegant: 'evening',
        soirée: 'evening', soiree: 'evening', dinner: 'evening',
        romantique: 'evening', cérémonie: 'evening', ceremonie: 'evening',
        bureau: 'work', workwear: 'work', business: 'work', preppy: 'work',
        sport: 'sport', sportif: 'sport', sportswear: 'sport', athleisure: 'sport',
        plage: 'beach', beach: 'beach',
        bohème: 'casual', boheme: 'casual', vintage: 'casual',
      };

      // Dédup CORE (Robe/Hauts/Bas/Chaussures) — même logique que Looks
      const CORE_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
      const coreKey = (it) => it?.id || (it?.name ? `${it.category}|${it.name}` : '');
      const bestPerCore = new Map();
      for (const o of generatedRaw) {
        const sig = (o.items || [])
          .filter(it => CORE_CATS.has(it?.category))
          .map(coreKey).filter(Boolean).sort().join('||');
        if (!sig) continue;
        const ex = bestPerCore.get(sig);
        if (!ex || (o.score || 0) > (ex.score || 0)) bestPerCore.set(sig, o);
      }
      const dedupedSorted = Array.from(bestPerCore.values())
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, maxTravelLooks);

      // Reroll — décale l'ordre selon regenCounter pour varier sans regen
      const offset = regenCounter % Math.max(1, dedupedSorted.length);
      const rolled = offset === 0 ? dedupedSorted : [
        ...dedupedSorted.slice(offset),
        ...dedupedSorted.slice(0, offset),
      ];

      // Convertir en structure attendue par le rendu existant
      const base = rolled.map((o, i) => ({
        id: o.id || `tlook_${i}`,
        name: `Look ${i + 1}`,
        occasion: TAG_TO_OCC[(o.bestTag || '').toLowerCase()] || tgt.occasion ? TAG_TO_OCC[tgt.occasion] || 'casual' : 'casual',
        score: o.score || 70,
        items: (o.items || []).map(it => it.id),
        itemDetails: o.items || [],
        cohesion: o.cohesion,
        tagMatches: o.tagMatches,
        bestTag: o.bestTag,
        missingItems: [],
      }));
      console.log(`[TRAVEL] OutfitEngine generated ${generatedRaw.length} → dedup ${dedupedSorted.length} → final ${base.length} looks (seed=${regenCounter}, target=${tgt.style}/${tgt.occasion})`);

    // ════════════════════════════════════════════════════════════
    // ÉTAPE 1 : Attacher des suggestions catalogue LOCALEMENT
    // (pas d'appel Claude → pas de quota IA consommé → toujours OK)
    // ════════════════════════════════════════════════════════════
    const attachLocalSuggestions = () => {
      const allCatalog = (catalog || []).filter(p =>
        p && p.image && p.price > 0 && (p.originalUrl || p.affiliateUrl || p.url)
      );
      console.log(`[TRAVEL] local suggestions catalog size: ${allCatalog.length}`);
      if (allCatalog.length === 0) return base;

      const travelExcludeIds = new Set();
      const tripTempCLocal = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;
      const STYLE_MAP_LOCAL = {
        soiree: 'Soirée', casual: 'Casual', business: 'Bureau',
        sport: 'Sport', plage: 'Casual', city: 'Casual',
      };

      const enriched = base.map((look, i) => {
        const lookItems = Array.isArray(look.itemDetails) ? look.itemDetails : [];
        const occasion = STYLE_MAP_LOCAL[look.occasion] || STYLE_MAP_LOCAL[trip.style] || 'Casual';

        // 1. Helper partagé : propose à la fois remplacements ET pièces manquantes
        let flatSugg = getOutfitCatalogSuggestions(
          { items: lookItems },
          allCatalog,
          {
            tempC: tripTempCLocal,
            occasion,
            gender: user?.gender || 'F',
            maxPicks: 8,
            perCategory: 3,
            alreadySuggestedIds: travelExcludeIds,
            includeReplace: true,
          }
        );

        // 2. Fallback suggestCatalogItems si pas assez
        if (flatSugg.length < 3) {
          const legacy = suggestCatalogItems({ items: lookItems }, allCatalog, 6);
          flatSugg = flatSugg.concat(
            legacy.filter(x =>
              !flatSugg.some(s => s.id === x.id) &&
              !travelExcludeIds.has(x.id)
            )
          );
          legacy.forEach(x => travelExcludeIds.add(x.id));
        }

        // 3. Dédup + top 5
        flatSugg = flatSugg
          .sort((a, b) => (b._stylistScore || 0) - (a._stylistScore || 0))
          .filter((p, idx, arr) => arr.findIndex(x => x.id === p.id) === idx)
          .slice(0, 5);

        // ═══ FIX DÉDUP GLOBAL : ajouter TOUS les articles suggérés à travelExcludeIds ═══
        // Avant : seul le fallback legacy était ajouté → les looks suivants pouvaient
        //         re-proposer les mêmes articles.
        flatSugg.forEach(p => { if (p.id) travelExcludeIds.add(p.id); });

        if (i === 0) {
          const missingList = Array.isArray(look.missing) ? look.missing : [];
          console.log(`[TRAVEL] look#0: items=${lookItems.length}, missing=[${missingList.map(m => typeof m === 'string' ? m : m.category).join(',')}], suggestions=${flatSugg.length}`);
        }

        return {
          ...look,
          // ...look spread already inclut missing s'il existe ; pas besoin de
          // le re-mentionner (et `missing` n'est pas declare dans ce scope).
          catalogSuggestions: flatSugg,
        };
      });

      return enriched;
    };

    const baseWithSuggs = attachLocalSuggestions();
    setGeneratedLooks(baseWithSuggs);

    // ═══ STOCKER EN CACHE (seulement si pas de regen forcé) ═══
    if (regenCounter === 0) {
      try {
        const { makeKey, setCache } = require('../services/LooksCacheService');
        const tempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;
        const cacheKey = makeKey('travel', packed, catalog, tempC, trip.style, maxTravelLooks);
        setCache(cacheKey, baseWithSuggs);
      } catch {}
    }

    // ════════════════════════════════════════════════════════════
    // ÉTAPE 2 : Enrichissement IA (tips Claude) — si quota OK
    // ════════════════════════════════════════════════════════════
    const enrichWithAI = async () => {
      // v1.12.105 — Quota AI retiré (n'incrémente que pour Claude Vision sur
      // ajout d'article). Cache 24h pour réduire les coûts Claude.
      try {
        const dest  = trip.destination || 'voyage';
        const style = trip.style || 'city';
        const itemNames = packed.slice(0, 8).map(p => p.name).join(', ');

        // 1. Tips IA via Claude — avec cache AsyncStorage 24h
        const tipsCacheKey = `outfit_travel_tips_${dest.slice(0,40)}_${style}_${itemNames.slice(0,80)}_${base.length}`;
        let tips = [];
        try {
          const cached = await AsyncStorage.getItem(tipsCacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && Date.now() - (parsed.ts || 0) < 24 * 3600 * 1000 && Array.isArray(parsed.tips)) {
              tips = parsed.tips;
            }
          }
        } catch {}

        if (tips.length === 0) {
          try {
            const res = await fetch('https://api.anthropic.com/v1/messages', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                model: 'claude-haiku-4-5-20251001',
                max_tokens: 400,
                messages: [{ role: 'user', content:
                  `Styliste mode. Voyage ${dest} style ${style}. Articles: ${itemNames}.\nDonne ${Math.min(base.length, 4)} conseils ultra-courts (max 12 mots), numérotés 1. 2. 3. 4.`
                }]
              })
            });
            const data = await res.json();
            tips = (data.content?.[0]?.text || '').split('\n').filter(l => l.match(/^\d/));
            if (tips.length > 0) {
              try { await AsyncStorage.setItem(tipsCacheKey, JSON.stringify({ ts: Date.now(), tips })); } catch {}
            }
          } catch(e) { console.warn('[TRAVEL] tips error:', e.message); }
        }

        // Enrichir le catalogue avec les fallbacks Zalando si vide
        const gender = user?.gender || 'F';
        const allCatalog = [...(catalog || [])];

        // Mapping trip.style → occasion IA
        const STYLE_TO_OCCASION = {
          soiree:   'Soirée',
          casual:   'Casual',
          business: 'Bureau',
          sport:    'Sport',
          plage:    'Casual',
          city:     'Casual',
        };

        // Température réelle estimée de destination (fallback 20°C)
        const tripTempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 20;

        // Set partagé pour varier les suggestions entre tenues voyage
        const travelExcludeIds = new Set();

        const enriched = baseWithSuggs.map((look, i) => {
          const lookItems = Array.isArray(look.itemDetails) ? look.itemDetails : [];
          const occasion = STYLE_TO_OCCASION[look.occasion] || STYLE_TO_OCCASION[trip.style] || 'Casual';

          // Score IA affiné
          const aiScore = lookItems.length >= 2
            ? scoreOutfit(lookItems, tripTempC, occasion)
            : look.score;

          return {
            ...look,
            score: aiScore,
            aiTip: tips[i]?.replace(/^\d+\.\s*/, '') || look.aiTip,
            tempC: tripTempC,
          };
        });

        setGeneratedLooks(enriched);
      } catch(e) {
        console.warn('[TRAVEL] enrichWithAI error:', e.message);
        // En cas d'erreur, garder les suggestions locales (déjà affichées)
      }
    };

    // v1.12.105 — Plus de check quota IA pour les tips voyage (le quota ne
    // s'incrémente que pour Claude Vision sur ajout d'article). Cache 24h
    // dans enrichWithAI limite les coûts.
    enrichWithAI();

    // 🎨 Analyser les articles packés avec Claude Vision en arrière-plan
    if (packed.length > 0 && user?.accessToken) {
      analyzeWardrobeInBackground(packed, user.accessToken).then(results => {
        if (results && results.length > 0) {
          console.log(`[AIStylist-Travel] ${results.length} articles voyage analysés`);
        }
      }).catch(() => {});
    }
    }, 0); // fin setTimeout — calcul déféré au prochain tick

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packed, trip.style, trip.weather, trip.destination, regenCounter, catalog, colorBank, materialBank]);

  // Catégories manquantes + suggestions recalculées automatiquement
  const { missingCats, suggestions } = React.useMemo(() => {
    const packedCats = new Set(packed.map(p => p.category));
    const ESSENTIAL_CATS = {
      city:    ['Hauts', 'Bas', 'Chaussures', 'Vestes'],
      beach:   ['Hauts', 'Bas', 'Chaussures', 'Accessoires'],
      mountain:['Hauts', 'Bas', 'Chaussures', 'Vestes'],
      sport:   ['Hauts', 'Bas', 'Chaussures'],
      business:['Hauts', 'Bas', 'Chaussures', 'Vestes', 'Sacs'],
    };
    const essentials  = ESSENTIAL_CATS[trip.style] || ESSENTIAL_CATS.city;
    const missingCats = essentials.filter(c => !packedCats.has(c));
    const packedIds   = new Set(packed.map(p => p.id));

    // ═══ Utilise le helper central detectCategoryByName (même logique partout) ═══
    const { detectCategoryByName, filterByGender } = require('../services/GenderFilter');

    const suggestions = missingCats.flatMap(cat => {
      return catalog
        .filter(p => {
          if (packedIds.has(p.id)) return false;
          // 1. Catégorie explicite match ?
          if (p.category === cat) {
            // Double-check via le nom : si le nom indique une AUTRE cat → rejet
            const detected = detectCategoryByName(p);
            if (detected && detected !== cat) return false;
            return true;
          }
          // 2. Sinon détection par nom
          const detected = detectCategoryByName(p);
          return detected === cat;
        })
        .slice(0, 2);
    }).slice(0, 8);

    // ═══ FILTRE GENRE : garde articles du bon genre + unisexes uniquement ═══
    let finalSuggestions = suggestions;
    try {
      const ug = user?.gender;
      if (ug && ['M', 'F'].includes(ug)) {
        const before = finalSuggestions.length;
        finalSuggestions = filterByGender(finalSuggestions, ug);
        if (finalSuggestions.length !== before) {
          console.log(`[TRAVEL] gender filter sur suggestions: ${finalSuggestions.length}/${before} (target=${ug})`);
        }
      }
    } catch (e) {
      console.warn('[TRAVEL] gender filter error:', e.message);
    }

    return { missingCats, suggestions: finalSuggestions };
  }, [packed, catalog, trip.style, user?.gender]);

  const dontForget = DONT_FORGET_BY_STYLE[trip.style] || [];
  const weatherInfo = WEATHER_PROFILES[currentWeather.key] || WEATHER_PROFILES[trip.weather];
  // Support multi-styles : récupère tous les styles du voyage
  const tripStyles = Array.isArray(trip.styles) && trip.styles.length > 0
    ? trip.styles
    : (trip.style ? [trip.style] : []);
  const stylesInfo = tripStyles
    .map(sid => TRIP_STYLES.find(s => s.id === sid))
    .filter(Boolean);
  const styleInfo = stylesInfo[0]; // pour rétrocompatibilité d'autres usages

  // Basculer la sélection d'un article
  const toggleSelect = (item) => {
    if (packed.find(p => p.id === item.id)) return; // déjà emporté
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  // Confirmer l'ajout de tous les articles sélectionnés
  const confirmAddSelected = () => {
    const toAdd = wardrobe.filter(item =>
      selectedItems.has(item.id) && !packed.find(p => p.id === item.id)
    );
    if (toAdd.length === 0) { setShowWardrobe(false); return; }

    // Vérifier la limite du tier
    const totalAfter = packed.length + toAdd.length;
    if (totalAfter > maxPacked) {
      const { Alert } = require('react-native');
      const available = Math.max(0, maxPacked - packed.length);
      Alert.alert(
        t('travel_max_packed_title').replace('{max}', maxPacked),
        available > 0
          ? t('travel_max_packed_some').replace('{n}', available).replace('{s}', available > 1 ? 's' : '')
          : t('travel_max_packed_full'),
        [{ text: 'OK' }]
      );
      // Ajouter seulement ce qui rentre
      if (available === 0) { setShowWardrobe(false); return; }
      setPacked(prev => [...prev, ...toAdd.slice(0, available)]);
    } else {
      setPacked(prev => [...prev, ...toAdd]);
    }
    setSelectedItems(new Set());
    setShowWardrobe(false);
  };

  // Supprimer un article de la valise
  const removeFromBag = (itemId) => {
    setPacked(prev => prev.filter(p => p.id !== itemId));
  };

  // Réinitialiser sélection à l'ouverture de la modal
  const openWardrobeModal = () => {
    setSelectedItems(new Set());
    setWardrobeSearch('');
    setWardrobeFilterCat('Tout');
    setShowWardrobe(true);
  };

  return (
    <>
    <SafeAreaView style={s.safe}>
            <View style={[s.topbar, { paddingTop: topPadding + 8 }]}>
        <BackButton onPress={onBack} />
        <View style={{ flex:1, marginLeft: 10 }}>
          <Text style={s.title}>{trip.name}</Text>
          <Text style={s.subtitle}>{trip.destination} · {trip.days} {t('days_count')}</Text>
        </View>
      </View>
      <Divider />

      {/* Header météo : v1.12.147 — affiche la plage de température à destination */}
      <View style={s.weatherBar}>
        <View style={s.weatherLeft}>
          <Text style={s.weatherIcon}>{weatherInfo?.icon}</Text>
          <View>
            <Text style={s.weatherLabel}>{weatherInfo?.label} · {trip.destination}</Text>
            <Text style={s.weatherTemp}>
              {(currentWeather.tempMin != null && currentWeather.tempMax != null)
                ? t('travel_weather_range').replace('{min}', currentWeather.tempMin).replace('{max}', currentWeather.tempMax)
                : (currentWeather.temp != null ? `${currentWeather.temp}°C` : (weatherInfo?.temp || ''))}
              {trip.departure ? ` · ${formatTripMonth(trip.departure)}` : ''}
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
          {stylesInfo.slice(0, 3).map(si => (
            <View key={si.id} style={s.styleBadge}>
              <Text style={s.styleBadgeText}>{si.icon} {si.labelKey ? t(si.labelKey) : si.label}</Text>
            </View>
          ))}
          {stylesInfo.length > 3 && (
            <View style={s.styleBadge}>
              <Text style={s.styleBadgeText}>+{stylesInfo.length - 3}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Tabs — v1.12.137 : onglet "À ne pas oublier" supprimé */}
      <View style={s.tabs}>
        {[
          { key:'bag',   label:`${t('whats_in_my_bag')} (${packed.length})` },
          { key:'looks', label:`${t('travel_looks')} (${generatedLooks.length})` },
        ].map(tb => (
          <TouchableOpacity key={tb.key} style={[s.tab, tab===tb.key && s.tabActive]} onPress={() => setTab(tb.key)}>
            <Text style={[s.tabLabel, tab===tb.key && s.tabLabelActive]}>{tb.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={s.tabLine}>
        <View style={[s.tabUnder, { left: tab==='bag' ? '0%' : '50%', width: '50%' }]} />
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: spacing.xl, paddingBottom: 32 }}
        refreshControl={
          <ThemedRefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.textFaint}
          />
        }
      >

        {/* ══════════ TAB : MA VALISE — Design premium ══════════ */}
        {tab === 'bag' && (
          <>
            {/* Hub header */}
            <View style={s.travelHub}>
              <View style={s.travelHubLeft}>
                <Text style={s.travelHubBadge}>{t('travel_hub_badge_bag')}</Text>
                <Text style={s.travelHubTitle}>{t('travel_hub_your_items')}</Text>
                <Text style={s.travelHubSub}>
                  {packed.length > 0
                    ? (isFinite(maxPacked) && maxPacked < 999
                        ? t('travel_hub_packed_summary_max').replace('{n}', packed.length).replace('{max}', maxPacked).replace('{s}', packed.length > 1 ? 's' : '').replace('{dest}', trip.destination)
                        : t('travel_hub_packed_summary').replace('{n}', packed.length).replace('{s}', packed.length > 1 ? 's' : '').replace('{dest}', trip.destination))
                    : t('travel_hub_select_what')}
                </Text>
              </View>
              <TouchableOpacity style={s.travelAddBtn} onPress={openWardrobeModal} activeOpacity={0.85}>
                <Text style={s.travelAddBtnPlus}>＋</Text>
                <Text style={s.travelAddBtnText}>{t('travel_add')}</Text>
              </TouchableOpacity>
            </View>

            {packed.length === 0 ? (
              <View style={s.travelEmptyState}>
                <Text style={s.travelEmptyIcon}>🧳</Text>
                <Text style={s.travelEmptyTitle}>{t('travel_empty_bag_title')}</Text>
                <Text style={s.travelEmptyDesc}>
                  {t('travel_empty_bag_desc')}
                </Text>
                <TouchableOpacity style={s.travelEmptyCta} onPress={openWardrobeModal}>
                  <Text style={s.travelEmptyCtaText}>{t('travel_empty_bag_cta')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={s.packedGrid}>
                {packed.map(item => (
                  <View key={`packed_${item.id || item.name || Math.random()}`} style={s.packedItem}>
                    <View style={s.packedItemThumb}>
                      {item.image || item.image_url
                        ? <Image source={{ uri: item.image || item.image_url }} style={s.packedItemPhoto} />
                        : <Text style={s.packedItemEmoji}>{item.emoji || '👗'}</Text>}
                      {item.ai_metadata?.analyzedAt && (
                        <View style={s.packedItemAiDot}>
                          <Text style={s.packedItemAiText}>✨</Text>
                        </View>
                      )}
                    </View>
                    <Text style={s.packedItemName} numberOfLines={1}>{item.name}</Text>
                    <Text style={s.packedItemColor}>{item.color}</Text>
                    <TouchableOpacity style={s.packedItemDel} onPress={() => removeFromBag(item.id)}>
                      <Text style={s.packedItemDelTxt}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}

            {/* Conseil météo */}
            {packed.length > 0 && weatherInfo && (
              <View style={s.weatherAdvice}>
                <Text style={s.weatherAdviceIcon}>{weatherInfo?.icon}</Text>
                <Text style={s.weatherAdviceText}>{weatherInfo?.advice}</Text>
              </View>
            )}

            {/* Sections par catégorie manquante */}
            {missingCats.length > 0 && (
              <View style={s.missingSectionBox}>
                <Text style={s.missingSectionTitle}>{t('travel_missing_in_bag')}</Text>
                <View style={s.missingPillsRow}>
                  {missingCats.map(cat => (
                    <View key={cat} style={s.missingCatPill}>
                      <Text style={s.missingCatPillText}>{cat}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Suggestions catalogue groupées par catégorie manquante */}
            {loadingCat && (
              <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:12 }}>
                <ActivityIndicator size="small" color="#999" />
                <Text style={{ fontSize:12, color:'#999' }}>{t('travel_searching_items')}</Text>
              </View>
            )}
            {(() => {
              // ═══ Tracking GLOBAL des produits entre toutes les sections ═══
              // Un produit ne peut apparaître que DANS UNE seule catégorie
              // même s'il matche plusieurs (ex: "chemise veste" → Hauts puis exclu de Vestes)
              // ═══ Clé : nom normalisé (sans taille) + brand + prix ═══
              // Permet de dédupliquer les "tailles fantômes" iQueens.
              const productKey = (p) => {
                const name  = normalizedNameKey(p?.name || '');
                const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
                const price = Number(p?.price) || 0;
                return `np:${brand}|${name}|${price}`;
              };
              const globalSeenIds = new Set(packed.map(x => productKey(x)));
              return missingCats.map(cat => {
                const defaultVisible = 4;
                const visibleCount = visibleByCat[cat] || defaultVisible;

                // Filtre : catégorie match + pas déjà affiché ailleurs + pas dans la valise
                const catItems = catalog.filter(p => {
                  if (p.category !== cat) return false;
                  if (globalSeenIds.has(productKey(p))) return false;
                  return true;
                });

                if (catItems.length === 0) return null;

                const displayed = catItems.slice(0, visibleCount);
                // Marquer les items affichés comme "vus globalement"
                displayed.forEach(p => globalSeenIds.add(productKey(p)));

                const hasMore = catItems.length > displayed.length;

                return (
                  <View key={cat} style={s.suggestSection}>
                    <Text style={s.suggestSectionTitle}>
                      {cat === 'Chaussures' ? '👟' : cat === 'Vestes' ? '🧥' : cat === 'Sacs' ? '👜' : cat === 'Accessoires' ? '✨' : cat === 'Hauts' ? '👚' : cat === 'Bas' ? '👖' : '👗'}
                      {' '}{t('travel_cat_for_trip').replace('{cat}', (() => { const M={'Robes':'cat_dresses','Hauts':'cat_tops','Vestes':'cat_jackets','Bas':'cat_bottoms','Chaussures':'cat_shoes','Sacs':'cat_bags','Accessoires':'cat_accessories','Bijoux':'cat_jewelry'}; return M[cat] ? t(M[cat]) : cat; })()).replace('{n}', displayed.length).replace('{plus}', hasMore ? '+' : '')}
                    </Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ gap: 12, paddingVertical: 8 }}
                      // v1.12.909 — Fix scroll left/right web (overflow CSS explicite)
                      style={Platform.OS === 'web' ? { overflowX: 'auto', overflowY: 'hidden' } : undefined}>
                      {displayed.map(p => {
                        // v1.12.909 — Badge dynamique selon partenaire (avant: hardcodé H&M/Z/A)
                        const PARTNER_BADGE = {
                          iqueens: { bg: '#1a1f36', txt: 'IQ' },
                          tous: { bg: '#D4AF37', txt: 'TOUS' },
                          suit_negozi: { bg: '#000', txt: 'SUIT' },
                          ann_demeulemeester: { bg: '#000', txt: 'ANN' },
                          coutr: { bg: '#1a1f36', txt: 'COUTR' },
                          tamaris: { bg: '#6B2E2E', txt: 'TAM' },
                          drestige: { bg: '#0E0C0A', txt: 'DRE' },
                          etsy: { bg: '#F45800', txt: 'ETSY' },
                          cashmere: { bg: '#D4B896', txt: 'CB' },
                        };
                        const badge = PARTNER_BADGE[p.partner] || { bg: '#888', txt: String(p.partner || '?').slice(0, 4).toUpperCase() };
                        return (
                        <TouchableOpacity key={`sc_${p.id || p.name || Math.random()}`} style={s.suggestCard}
                          onPress={() => setSelectedProduct(p)} activeOpacity={0.85}>
                          <View style={s.suggestCardImg}>
                            {p.image
                              ? <Image source={{ uri: p.image }} style={{ width:'100%', height:'100%', resizeMode:'cover' }} />
                              : <Text style={{ fontSize: 32 }}>{p.emoji || '👗'}</Text>}
                            <View style={[s.partnerBadge, { backgroundColor: badge.bg }]}>
                              <Text style={s.partnerBadgeTxt}>{badge.txt}</Text>
                            </View>
                          </View>
                          <View style={s.suggestCardInfo}>
                            <Text style={s.suggestCardCat}>{p.brand}</Text>
                            <Text style={s.suggestCardName} numberOfLines={2}>{p.name}</Text>
                            <Text style={s.suggestCardPrice}>{p.price} €</Text>
                          </View>
                          <TouchableOpacity
                            style={{ backgroundColor: badge.bg,
                              margin:6, borderRadius:6, paddingVertical:6, alignItems:'center' }}
                            onPress={() => setSelectedProduct(p)}>
                            <Text style={{ fontSize:10, fontWeight:'700', color:'#fff' }}>{t('travel_view_details')}</Text>
                          </TouchableOpacity>
                        </TouchableOpacity>
                      ); })}
                      {/* ═══ Carte "Voir plus" à droite (si plus d'items disponibles) ═══ */}
                      {hasMore && (
                        <TouchableOpacity
                          style={[s.suggestCard, {
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#FFF9E6',
                            borderWidth: 1.5,
                            borderColor: '#F5DEB3',
                            borderStyle: 'dashed',
                          }]}
                          onPress={() => setVisibleByCat(prev => ({ ...prev, [cat]: (prev[cat] || defaultVisible) + 4 }))}
                          activeOpacity={0.8}
                        >
                          <Text style={{ fontSize: 32, marginBottom: 4 }}>➡️</Text>
                          <Text style={{ fontSize: 12, fontWeight: '700', color: '#B8860B', textAlign: 'center' }}>
                            {t('travel_view_more')}
                          </Text>
                          <Text style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                            +{Math.min(4, catItems.length - displayed.length)}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </ScrollView>
                  </View>
                );
              });
            })()}

            {/* Autres suggestions si aucune catégorie manquante */}
            {missingCats.length === 0 && catalog.length > 0 && (
              <View style={s.suggestSection}>
                <Text style={s.suggestSectionTitle}>{t('travel_trending_items')}</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 12, paddingVertical: 8 }}
                  style={Platform.OS === 'web' ? { overflowX: 'auto', overflowY: 'hidden' } : undefined}>
                  {(() => {
                    // Dédup défensive : nom normalisé (sans taille iQueens) + brand + prix
                    const seenKeys = new Set();
                    const unique = catalog.filter(p => {
                      const k = `np:${String(p?.brand || p?.partner || '').toLowerCase()}|${normalizedNameKey(p?.name || '')}|${Number(p?.price) || 0}`;
                      if (seenKeys.has(k)) return false;
                      seenKeys.add(k);
                      return true;
                    });
                    return unique.slice(0, 6).map(p => (
                    <TouchableOpacity key={`sc_${p.id || p.name || Math.random()}`} style={s.suggestCard}
                      onPress={() => setSelectedProduct(p)} activeOpacity={0.85}>
                      <View style={s.suggestCardImg}>
                        {p.image
                          ? <Image source={{ uri: p.image }} style={{ width:'100%', height:'100%', resizeMode:'cover' }} />
                          : <Text style={{ fontSize: 32 }}>{p.emoji || '👗'}</Text>}
                        <View style={[s.partnerBadge, { backgroundColor:
                          p.partner === 'zalando' ? '#FF6900' : p.partner === 'asos' ? '#111' : '#E50010' }]}>
                          <Text style={s.partnerBadgeTxt}>
                            {p.partner === 'zalando' ? 'Z' : p.partner === 'asos' ? 'A' : 'HM'}
                          </Text>
                        </View>
                      </View>
                      <View style={s.suggestCardInfo}>
                        <Text style={s.suggestCardCat}>{p.category}</Text>
                        <Text style={s.suggestCardName} numberOfLines={2}>{p.name}</Text>
                        <Text style={s.suggestCardPrice}>{p.price} €</Text>
                      </View>
                    </TouchableOpacity>
                    ));
                  })()}
                </ScrollView>
              </View>
            )}
          </>
        )}

        {/* ══════════ TAB : LOOKS VOYAGE — Design premium inspiré de Looks ══════════ */}
        {tab === 'looks' && (
          <>
            {/* Hub header stylist */}
            <View style={s.travelHub}>
              <View style={s.travelHubLeft}>
                <Text style={s.travelHubBadge}>{t('travel_hub_badge_looks')}</Text>
                <Text style={s.travelHubTitle}>{t('travel_hub_your_outfits')}</Text>
                <Text style={s.travelHubSub}>
                  {generatedLooks.length > 0
                    ? t('travel_looks_count_summary').replace('{n}', generatedLooks.length).replace('{s}', generatedLooks.length > 1 ? 's' : '').replace('{p}', packed.length).replace('{ps}', packed.length > 1 ? 's' : '')
                    : packed.length >= 3
                      ? t('travel_generation_in_progress')
                      : packed.length > 0
                        ? t('travel_add_more_items').replace('{n}', Math.max(3 - packed.length, 1)).replace('{s}', 3 - packed.length > 1 ? 's' : '')
                        : t('travel_add_clothes_to_bag')}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {/* v1.12.119 — bouton reload (force regen) — visible des qu'il y a 1 item */}
                {packed.length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      console.log('[TRAVEL] 🔄 reload click — packed=', packed.length, 'looks=', generatedLooks.length);
                      try {
                        const { invalidateNamespace, clearCache } = require('../services/LooksCacheService');
                        invalidateNamespace?.('travel');
                        // Belt and suspenders : clear le cache memo entier
                        clearCache?.();
                      } catch {}
                      // Reset les looks → le useEffect main reverra packed et regenerera
                      setGeneratedLooks([]);
                      setRegenCounter(c => c + 1);
                    }}
                    activeOpacity={0.7}
                    style={{
                      width: 36, height: 36, borderRadius: 18,
                      backgroundColor: '#F9F7F3',
                      borderWidth: 1, borderColor: '#EDEBE8',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                    accessibilityLabel={t('travel_regen_a11y')}
                  >
                    <Text style={{ fontSize: 16, color: '#1A1814' }}>↻</Text>
                  </TouchableOpacity>
                )}
                {generatedLooks.length > 0 && (
                  <View style={s.travelHubStat}>
                    <Text style={s.travelHubStatNum}>
                      {Math.round(generatedLooks.reduce((s, c) => s + (c.score || 0), 0) / Math.max(1, generatedLooks.length))}
                    </Text>
                    <Text style={s.travelHubStatLabel}>{t('travel_avg_score')}</Text>
                  </View>
                )}
              </View>
            </View>

            {/* v1.12.103 — Bouton "Générer de nouvelles variations" supprimé */}
            {/* (les variantes sont déjà gérées par dédup CORE + tri DESC). */}

            {/* État vide élégant */}
            {packed.length === 0 && (
              <View style={s.travelEmptyState}>
                <Text style={s.travelEmptyIcon}>🧳</Text>
                <Text style={s.travelEmptyTitle}>{t('travel_empty_bag_title')}</Text>
                <Text style={s.travelEmptyDesc}>
                  {t('travel_empty_bag_desc')}
                </Text>
                <TouchableOpacity
                  style={s.travelEmptyCta}
                  onPress={() => setTab('bag')}
                >
                  <Text style={s.travelEmptyCtaText}>{t('travel_go_to_bag')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* v1.12.119 — Etat intermediaire : valise pleine mais pas encore
                 de looks generes. Donne un feedback (loader + reload) au lieu
                 d'un ecran vide. */}
            {packed.length > 0 && generatedLooks.length === 0 && (
              <View style={s.travelEmptyState}>
                <Text style={s.travelEmptyIcon}>✨</Text>
                <Text style={s.travelEmptyTitle}>{t('travel_generating_looks')}</Text>
                <Text style={s.travelEmptyDesc}>
                  {packed.length < 3
                    ? t('travel_add_more_for_variety').replace('{n}', 3 - packed.length).replace('{s}', 3 - packed.length > 1 ? 's' : '')
                    : t('travel_minimum_combo_hint')
                  }
                </Text>
                <TouchableOpacity
                  style={s.travelEmptyCta}
                  onPress={() => {
                    try {
                      const { invalidateNamespace, clearCache } = require('../services/LooksCacheService');
                      invalidateNamespace?.('travel');
                      clearCache?.();
                    } catch {}
                    setGeneratedLooks([]);
                    setRegenCounter(c => c + 1);
                  }}
                >
                  <Text style={s.travelEmptyCtaText}>{t('travel_relaunch_generation')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Looks avec OutfitCard premium */}
            {packed.length > 0 && generatedLooks.length > 0 && (() => {
              // ═══ v1.12.103 — Chips dynamiques (comme Looks) ═══
              // Pour chaque outfit, on compte son bestTag (sortie OutfitEngine).
              // Les chips s'auto-construisent depuis les tags réels présents.
              const normalizeTag = (raw) => {
                if (!raw || typeof raw !== 'string') return null;
                return raw.toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
              };
              const STOPWORDS = new Set([
                'basic', 'simple', 'tendance', 'trendy', 'moderne', 'actuel',
                'polyvalent', 'versatile', 'basique',
              ]);
              const chipCounts = {};
              for (const look of generatedLooks) {
                const tag = normalizeTag(look.bestTag);
                if (!tag || STOPWORDS.has(tag)) continue;
                chipCounts[tag] = (chipCounts[tag] || 0) + 1;
              }
              const dynamicChips = Object.entries(chipCounts)
                .sort((a, b) => b[1] - a[1])  // count desc
                .slice(0, 8)                   // top 8
                .map(([tag]) => tag);
              const FILTER_LABELS = ['Tout', ...dynamicChips];

              // Filtrage par chip sélectionné
              // v1.12.119 — tri par score DESC (meilleurs d'abord) dans tous
              // les onglets (Tout + chaque chip). Avant : ordre arbitraire
              // selon l'algo OutfitEngine, scores forts pas toujours en haut
              // du filtre.
              const filteredLooks = travelOccasion === 'Tout'
                ? generatedLooks
                : generatedLooks.filter(l => normalizeTag(l.bestTag) === travelOccasion);
              // v1.12.789 — Exclure looks avec CORE item warm-incompatible (tempC trip)
              const tripTemp = typeof trip?.estimatedTemp === 'number' ? trip.estimatedTemp : null;
              const _CORE = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
              // v1.12.804 — Aligné sur removedWarm (rendu OutfitCard) : tout CORE warm
              // retiré >22 = outfit non portable, à filtrer.
              const _isCoreWarmIncompat = (it) => {
                if (!it || !_CORE.has(it.category)) return false;
                const name = (it.name || '').toLowerCase();
                const mat = (it.ai_metadata?.material || '').toLowerCase();
                const isWarmMaterial = /pull|sweat|hoodie|cardigan|cachemire|laine|polaire|cashmere|mohair/.test(name)
                  || /laine|wool|cachemire|cashmere|mohair|polaire/.test(mat);
                if (!isWarmMaterial) return false;
                if (tripTemp > 25) return true;
                if (tripTemp > 22) return true;
                return false;
              };
              const tempFiltered = tripTemp !== null
                ? filteredLooks.filter(l => !((l.items || packed.filter(p => l.items?.includes(p.id))) || []).some(_isCoreWarmIncompat))
                : filteredLooks;
              const displayedLooks = [...tempFiltered].sort(
                (a, b) => (b.score || b._stylistScore || 0) - (a.score || a._stylistScore || 0)
              );

              return (
              <>
                <View style={s.travelSectionHeader}>
                  <Text style={s.travelSectionIcon}>✨</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.travelSectionTitle}>{t('travel_looks_with_bag')}</Text>
                    <Text style={s.travelSectionSub}>
                      {t('travel_outfits_count').replace('{n}', displayedLooks.length).replace('{s}', displayedLooks.length > 1 ? 's' : '')}
                      {travelOccasion !== 'Tout' ? t('travel_outfits_with_label').replace('{label}', travelOccasion.toLowerCase()) : t('travel_outfits_all_owned')}
                    </Text>
                  </View>
                </View>

                {/* ═══ Chips dynamiques (comme Looks) ═══ */}
                {FILTER_LABELS.length > 1 && (
                  <View style={s.travelOccWrap}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}
                      contentContainerStyle={s.travelOccRow}
                      style={Platform.OS === 'web' ? { overflowX: 'auto', overflowY: 'hidden' } : undefined}>
                      {FILTER_LABELS.map(occ => {
                        const count = occ === 'Tout'
                          ? generatedLooks.length
                          : (chipCounts[occ] || 0);
                        const label = occ === 'Tout'
                          ? t('travel_filter_all')
                          : occ.charAt(0).toUpperCase() + occ.slice(1);
                        return (
                          <TouchableOpacity key={occ}
                            style={[s.travelOccChip, travelOccasion === occ && s.travelOccChipActive]}
                            onPress={() => setTravelOccasion(occ)}>
                            <Text style={[s.travelOccLabel, travelOccasion === occ && s.travelOccLabelActive]}>
                              {label} · {count}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  </View>
                )}

                {displayedLooks.length === 0 ? (
                  <View style={{ padding: 24, alignItems: 'center' }}>
                    <Text style={{ fontSize: 14, color: '#666', textAlign: 'center' }}>
                      {t('travel_no_outfits_for_filter').replace('{label}', travelOccasion.toLowerCase())}
                    </Text>
                  </View>
                ) : displayedLooks.map((look, i) => {
                  const lookItems = look.itemDetails && look.itemDetails.length > 0
                    ? look.itemDetails
                    : packed.filter(p => look.items?.includes(p.id));
                  const lookId = `tlook_${i}_${look.id || i}`;
                  return (
                    <OutfitCard
                      key={lookId}
                      outfit={{
                        id: lookId,
                        items: lookItems,
                        score: look.score,
                        description: look.name || `Look ${i + 1}`,
                        occasion: look.occasion,
                        missing: look.missing || [],
                        suggestions: look.catalogSuggestions || [],
                      }}
                      tempC={typeof trip?.estimatedTemp === 'number' ? trip.estimatedTemp : null}
                      onSuggestPress={(p) => setSelectedProduct(p)}
                      onItemPress={(item) => setSelectedWardrobeItem(item)}
                      onLoadMore={handleLoadMoreTravelLook}
                      loadingMore={loadingMoreTravelFor === lookId}
                      onTryOn={handleTryOn}
                      style={{ marginBottom: spacing.lg }}
                    />
                  );
                })}
              </>
              );
            })()}

            {packed.length > 0 && packed.length < 3 && (
              <View style={[s.infoBox, { marginTop: spacing.md }]}>
                <Text style={s.infoBoxText}>{t('travel_more_items_hint')}</Text>
              </View>
            )}

            {/* ──── Section "Looks à shopper" pour ce voyage ──── */}
            {packed.length > 0 && catalog.length > 0 && missingCats.length > 0 && (
              <>
                <View style={[s.travelSectionHeader, { marginTop: spacing.xl }]}>
                  <Text style={s.travelSectionIcon}>🛍️</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.travelSectionTitle}>{t('travel_looks_to_shop')}</Text>
                    <Text style={s.travelSectionSub}>{t('travel_looks_to_shop_sub')}</Text>
                  </View>
                </View>
                {(() => {
                  // Construire 3-5 "looks à shopper" pour le voyage
                  const travelStyles = [
                    { label: 'Tenue complète ' + trip.destination, emoji: '✈️', wantsCatalog: missingCats.slice(0, 5) },
                  ];

                  // ═══ Clé pour dédup : nom normalisé (sans taille) + brand + prix ═══
                  const productKey = (p) => {
                    const name  = normalizedNameKey(p?.name || '');
                    const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
                    const price = Number(p?.price) || 0;
                    return `np:${brand}|${name}|${price}`;
                  };

                  // Prendre 5 à 10 produits du catalogue qui couvrent les catégories manquantes
                  const results = [];
                  const used = new Set();

                  // Tenue 1 : articles manquants + suggestions
                  const tenue1Items = [];
                  let tenue1Price = 0;
                  missingCats.slice(0, 8).forEach(cat => {
                    const cands = catalog.filter(p =>
                      p.category === cat && !used.has(productKey(p)) && p.image && p.price > 0
                    );
                    if (cands.length > 0) {
                      // Random pondéré
                      const scored = cands.map(p => {
                        let s = 50;
                        if (p.partner === 'cashmere' || p.partner === 'iqueens') s += 30;
                        if (p.discount > 20) s += 15;
                        return { p, s };
                      }).sort((a, b) => b.s - a.s).slice(0, 15);
                      const total = scored.reduce((x, y) => x + y.s, 0);
                      let rnd = Math.random() * total;
                      let chosen = scored[0].p;
                      for (const x of scored) { rnd -= x.s; if (rnd <= 0) { chosen = x.p; break; } }
                      used.add(productKey(chosen));
                      tenue1Items.push(chosen);
                      tenue1Price += chosen.price || 0;
                    }
                  });

                  // Afficher en card mixCombo
                  if (tenue1Items.length >= 3) {
                    return (
                      <View style={s.travelMixCombo}>
                        <View style={s.travelMixHeader}>
                          <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <Text style={{ fontSize: 22 }}>✈️</Text>
                              <Text style={s.travelMixLabel}>{t('travel_essentials_dest').replace('{dest}', trip.destination)}</Text>
                            </View>
                            <Text style={s.travelMixMeta}>
                              <Text style={{ fontWeight: '700', color: '#D4AF37' }}>+{tenue1Items.length}</Text> {t('travel_articles_to_shop')}
                            </Text>
                          </View>
                          <View style={s.travelMixPriceBadge}>
                            <Text style={s.travelMixPriceLabel}>{t('travel_cart_label')}</Text>
                            <Text style={s.travelMixPrice}>{Math.round(tenue1Price)} €</Text>
                          </View>
                        </View>

                        <ScrollView horizontal showsHorizontalScrollIndicator={false}
                          contentContainerStyle={s.travelMixItemsRow}
                          style={Platform.OS === 'web' ? { overflowX: 'auto', overflowY: 'hidden' } : undefined}>
                          {tenue1Items.map((item, idx) => (
                            <TouchableOpacity
                              key={`tshop_${idx}_${item.id}`}
                              style={s.travelMixItem}
                              onPress={() => setSelectedProduct(item)}
                              activeOpacity={0.85}
                            >
                              <View style={[s.travelMixItemBox, s.travelMixItemBoxNew]}>
                                {item.image
                                  ? <Image source={{ uri: item.image }} style={s.travelMixItemImg} />
                                  : <View style={s.travelMixItemPlaceholder}>
                                      <Text style={{ fontSize: 32 }}>{item.emoji || '👗'}</Text>
                                    </View>
                                }
                                <View style={s.travelMixNewBadge}>
                                  <Text style={s.travelMixNewBadgeText}>NEW</Text>
                                </View>
                              </View>
                              <Text style={s.travelMixItemName} numberOfLines={1}>{item.name}</Text>
                              <Text style={s.travelMixItemPrice}>{item.price} €</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>

                        <TouchableOpacity
                          style={s.travelMixCta}
                          onPress={() => {
                            if (tenue1Items.length > 0) setSelectedProduct(tenue1Items[0]);
                          }}
                        >
                          <Text style={s.travelMixCtaText}>
                            {t('travel_discover_articles').replace('{n}', tenue1Items.length)}
                          </Text>
                          <Text style={s.travelMixCtaArrow}>→</Text>
                        </TouchableOpacity>
                      </View>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            {/* ════════════════════════════════════════════════════════════
              v1.12.905 — Catalogue voyage (algorithme Complete my Look adapté)
              Filtre le catalogue par :
                - Température du voyage (trip.estimatedTemp ± tolérance)
                - Style du voyage (mapping trip.style → ai_metadata.occasions)
                - Genre user (M / F)
              Affiche les top items par catégorie pour aider à acheter en
              prévision du voyage. Aucun appel backend → 100% local + rapide.
              ════════════════════════════════════════════════════════════ */}
            {catalog.length > 0 && (() => {
              const tripTempC = typeof trip.estimatedTemp === 'number' ? trip.estimatedTemp : 18;
              const userGender = user?.gender || 'F';
              // Map trip.style → occasion-key utilisé dans ai_metadata.occasions
              const STYLE_TO_OCC = {
                beach: 'vacances', city: 'quotidien', mountain: 'sport',
                business: 'bureau', wedding: 'cérémonie', backpacker: 'weekend',
                luxury: 'cocktail', safari: 'vacances',
              };
              const tripOccasion = STYLE_TO_OCC[trip.style] || 'quotidien';
              // Catégories principales à proposer pour un voyage (homme + femme)
              const TARGET_CATS = userGender === 'M'
                ? ['Hauts', 'Bas', 'Vestes', 'Chaussures', 'Manteaux', 'Accessoires']
                : ['Hauts', 'Bas', 'Robes', 'Vestes', 'Chaussures', 'Manteaux', 'Sacs', 'Accessoires'];

              // Filtrage + scoring par item (gender + temp + occasion)
              const matchesGender = (it) => {
                const aiG = it.ai_metadata?.gender;
                const declG = it.gender;
                const target = userGender;
                if (aiG && aiG !== 'unisex' && aiG !== target) return false;
                if (declG && declG !== 'unisex' && declG !== target) return false;
                return true;
              };
              const matchesTemp = (it) => {
                const range = it.ai_metadata?.temperatureRange;
                if (!Array.isArray(range) || range.length < 2) return true; // pas d'info → neutre
                const [lo, hi] = range;
                if (typeof lo !== 'number' || typeof hi !== 'number') return true;
                return tripTempC >= lo - 4 && tripTempC <= hi + 4; // tolérance ± 4°C
              };
              const scoreItem = (it) => {
                let s = 50; // baseline (items sans ai_metadata ne sont pas écrasés)
                const occMatch = it.ai_metadata?.occasions?.[tripOccasion];
                if (typeof occMatch === 'number') s += occMatch; // 0-100 typique
                if (it.isTrending) s += 25;
                // Bonus saisonnalité si elle matche temp
                const seasons = it.ai_metadata?.seasonality;
                if (Array.isArray(seasons)) {
                  const currentSeason = tripTempC >= 25 ? 'été' : tripTempC >= 15 ? 'printemps' : tripTempC >= 5 ? 'automne' : 'hiver';
                  if (seasons.includes(currentSeason)) s += 15;
                }
                // v1.12.909 — petite randomization pour mixer partenaires à score égal
                s += Math.random() * 5;
                return s;
              };

              const byCategory = {};
              for (const cat of TARGET_CATS) {
                const items = catalog
                  .filter(it => it && it.category === cat && it.image && it.price > 0)
                  .filter(matchesGender)
                  .filter(matchesTemp)
                  .map(it => ({ ...it, _travelScore: scoreItem(it) }))
                  .sort((a, b) => b._travelScore - a._travelScore)
                  .slice(0, 8); // top 8 par catégorie
                if (items.length > 0) byCategory[cat] = items;
              }

              if (Object.keys(byCategory).length === 0) return null;
              const totalSuggestions = Object.values(byCategory).reduce((acc, arr) => acc + arr.length, 0);

              return (
                <>
                  <View style={[s.travelSectionHeader, { marginTop: spacing.xl }]}>
                    <Text style={s.travelSectionIcon}>🧳</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={s.travelSectionTitle}>
                        {t('travel_catalog_compatible_title') || 'Catalogue voyage (compatible)'}
                      </Text>
                      <Text style={s.travelSectionSub}>
                        {(t('travel_catalog_compatible_sub') || '{n} articles · {temp}°C · {style}')
                          .replace('{n}', String(totalSuggestions))
                          .replace('{temp}', String(tripTempC))
                          .replace('{style}', String(trip.style || 'city'))}
                      </Text>
                    </View>
                  </View>
                  {Object.entries(byCategory).map(([cat, items]) => (
                    <View key={`travelcat_${cat}`} style={{ marginBottom: spacing.lg }}>
                      <Text style={[s.travelMixLabel, { paddingHorizontal: 16, marginBottom: 8 }]}>
                        {cat} · <Text style={{ fontWeight: '400', color: '#888' }}>{items.length}</Text>
                      </Text>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ gap: 10, paddingHorizontal: 16 }}
                        // v1.12.908 — Fix scroll left/right sur web
                        style={Platform.OS === 'web' ? { overflowX: 'auto', overflowY: 'hidden' } : undefined}
                      >
                        {items.map((item, idx) => (
                          <TouchableOpacity
                            key={`travelcat_${cat}_${idx}_${item.id}`}
                            style={s.travelMixItem}
                            onPress={() => setSelectedProduct(item)}
                            activeOpacity={0.85}
                          >
                            <View style={s.travelMixItemBox}>
                              {item.image
                                ? <Image source={{ uri: item.image }} style={s.travelMixItemImg} />
                                : <View style={s.travelMixItemPlaceholder}>
                                    <Text style={{ fontSize: 32 }}>{item.emoji || '👗'}</Text>
                                  </View>
                              }
                              {item.isTrending && (
                                <View style={[s.travelMixNewBadge, { backgroundColor: '#D4AF37' }]}>
                                  <Text style={s.travelMixNewBadgeText}>★</Text>
                                </View>
                              )}
                            </View>
                            <Text style={s.travelMixItemName} numberOfLines={1}>{item.brand || item.name}</Text>
                            <Text style={s.travelMixItemPrice}>{Math.round(item.price)} €</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  ))}
                </>
              );
            })()}
          </>
        )}

        {/* ── TAB : À NE PAS OUBLIER (v1.12.137 : supprimé) ── */}
        {false && tab === 'tips' && (() => {
          // Calcul de la checklist catégorisée selon le style du voyage
          const checklistByCat = getChecklistForStyle(trip.style);
          const allItems = Object.values(checklistByCat).flatMap(c => c.items);
          const checkedCount = allItems.filter(it => checkedItems[it]).length;
          const pct = allItems.length > 0 ? Math.round((checkedCount / allItems.length) * 100) : 0;

          // Détection des catégories manquantes dans la valise
          const packedCats = new Set((packed || []).map(p => p.category));
          const IMPORTANT_CATS = ['Hauts', 'Bas', 'Chaussures', 'Vestes', 'Sacs'];
          const missingWardrobeCats = IMPORTANT_CATS.filter(c => !packedCats.has(c));

          // Règle des X jours : nombre nécessaire selon durée
          const daysNeeded = {
            hauts:     Math.max(1, Math.ceil(trip.days / 2)),
            bas:       Math.max(1, Math.ceil(trip.days / 3)),
            chaussures:Math.min(2, Math.max(1, Math.ceil(trip.days / 5))),
          };

          return (
          <>
            {/* ═══ HERO : progression globale ═══ */}
            <View style={s.checklistHero}>
              <View style={s.checklistHeroTop}>
                <View>
                  <Text style={s.checklistHeroBadge}>{t('travel_checklist_badge')}</Text>
                  <Text style={s.checklistHeroTitle}>{t('travel_checklist_progress').replace('{checked}', checkedCount).replace('{total}', allItems.length)}</Text>
                  <Text style={s.checklistHeroSub}>
                    {pct === 100 ? t('travel_checklist_done') : pct >= 70 ? t('travel_checklist_almost') : t('travel_checklist_more')}
                  </Text>
                </View>
                <View style={s.checklistHeroCircle}>
                  <Text style={s.checklistHeroCircleNum}>{pct}<Text style={{ fontSize:10 }}>%</Text></Text>
                </View>
              </View>
              <View style={s.checklistProgressBar}>
                <View style={[s.checklistProgressFill, { width: `${pct}%` }]} />
              </View>
            </View>

            {/* ═══ Plage de temperature sur la duree du sejour (v1.12.135) ═══ */}
            {(currentWeather.tempMin != null && currentWeather.tempMax != null) ? (
              <View style={s.tempRangeCard}>
                <View style={s.tempRangeHeader}>
                  <Text style={s.tempRangeIcon}>{weatherInfo?.icon || '🌡️'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.tempRangeBadge}>{t('travel_weather_forecast_badge').replace('{dest}', trip.destination)}</Text>
                    <Text style={s.tempRangeTitle}>
                      {t('travel_weather_range').replace('{min}', currentWeather.tempMin).replace('{max}', currentWeather.tempMax)}
                    </Text>
                    <Text style={s.tempRangeSub}>
                      {t('travel_over_n_days').replace('{n}', trip.days).replace('{s}', trip.days > 1 ? 's' : '')}
                      {trip.departure ? ` · ${formatTripDateRange(trip.departure, trip.returnDate)}` : ''}
                    </Text>
                  </View>
                </View>
              </View>
            ) : null}

            {/* ═══ ANALYSE VALISE (dressing) ═══ */}
            {missingWardrobeCats.length > 0 && (
              <View style={s.wardrobeAlertCard}>
                <View style={s.wardrobeAlertHeader}>
                  <Text style={s.wardrobeAlertIcon}>⚠️</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.wardrobeAlertTitle}>{t('travel_missing_in_suitcase')}</Text>
                    <Text style={s.wardrobeAlertSub}>
                      {t('travel_missing_for_days').replace('{n}', trip.days).replace('{s}', trip.days > 1 ? 's' : '')}
                    </Text>
                  </View>
                </View>
                <View style={s.wardrobeAlertChips}>
                  {missingWardrobeCats.map(c => (
                    <View key={c} style={s.wardrobeAlertChip}>
                      <Text style={s.wardrobeAlertChipText}>{c}</Text>
                    </View>
                  ))}
                </View>
                <TouchableOpacity style={s.wardrobeAlertCTA} onPress={() => setTab('looks')}>
                  <Text style={s.wardrobeAlertCTAText}>{t('travel_see_shopping_suggestions')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* ═══ RÈGLE DES JOURS ═══ */}
            <View style={s.ruleCard}>
              <Text style={s.ruleCardTitle}>{t('travel_rule_for_days').replace('{n}', trip.days).replace('{s}', trip.days > 1 ? 's' : '')}</Text>
              <View style={s.ruleRow}>
                <Text style={s.ruleNum}>{daysNeeded.hauts}</Text>
                <Text style={s.ruleLabel}>{t('travel_rule_tops')}</Text>
                <Text style={s.ruleDivider}>·</Text>
                <Text style={s.ruleNum}>{daysNeeded.bas}</Text>
                <Text style={s.ruleLabel}>{t('travel_rule_bottoms')}</Text>
                <Text style={s.ruleDivider}>·</Text>
                <Text style={s.ruleNum}>{daysNeeded.chaussures}</Text>
                <Text style={s.ruleLabel}>{t('travel_rule_pairs')}</Text>
              </View>
              <Text style={s.ruleFoot}>{t('travel_rule_footer')}</Text>
            </View>

            {/* ═══ BONS PLANS IA ═══ */}
            <View style={s.aiTipsCard}>
              <View style={s.aiTipsHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={s.aiTipsBadge}>{t('travel_ai_tips_badge')}</Text>
                  <Text style={s.aiTipsTitle}>{t('travel_ai_tips_title')}</Text>
                  <Text style={s.aiTipsSub}>
                    {t('travel_ai_tips_sub')}
                  </Text>
                </View>
                <TouchableOpacity
                  style={[s.aiTipsBtn, (aiTipsLoading || aiTipsOnCooldown) && { opacity: 0.5 }]}
                  disabled={aiTipsLoading || aiTipsOnCooldown}
                  onPress={fetchAITips}
                >
                  {aiTipsLoading ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : aiTipsOnCooldown ? (
                    <Text style={s.aiTipsBtnText}>
                      {t('travel_ai_tips_cooldown_short').replace('{min}', Math.ceil(aiTipsCooldownLeft / 60000))}
                    </Text>
                  ) : (
                    <Text style={s.aiTipsBtnText}>{aiTips.length > 0 ? t('travel_ai_tips_refresh') : t('travel_ai_tips_generate_btn')}</Text>
                  )}
                </TouchableOpacity>
              </View>

              {aiTipsError && (
                <Text style={s.aiTipsErr}>{aiTipsError}</Text>
              )}

              {aiTips.length > 0 && (
                <View style={{ marginTop: 14 }}>
                  {aiTips.map((tip, i) => (
                    <View key={i} style={s.aiTipRow}>
                      <Text style={s.aiTipIcon}>{tip.icon || '💡'}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={s.aiTipCat}>{(tip.category || '').toUpperCase()}</Text>
                        <Text style={s.aiTipText}>{tip.text}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {aiTips.length === 0 && !aiTipsLoading && !aiTipsError && (
                <View style={{ marginTop: 12, alignItems: 'center' }}>
                  <Text style={{ fontSize: 30, marginBottom: 6 }}>🎯</Text>
                  <Text style={{ fontSize: 12, color: '#666', textAlign: 'center', lineHeight: 16 }}>
                    {t('travel_ai_tips_empty')}
                  </Text>
                </View>
              )}
            </View>

            {/* ═══ CHECKLIST PAR CATÉGORIE ═══ */}
            {Object.entries(checklistByCat).map(([catKey, cat]) => {
              const catChecked = cat.items.filter(it => checkedItems[it]).length;
              const catPct = cat.items.length > 0 ? Math.round((catChecked / cat.items.length) * 100) : 0;
              return (
                <View key={catKey} style={s.catSection}>
                  <View style={s.catSectionHeader}>
                    <View style={[s.catSectionIcon, { backgroundColor: cat.color + '15', borderColor: cat.color + '40' }]}>
                      <Text style={{ fontSize: 18 }}>{cat.icon}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={s.catSectionTitle}>{cat.label}</Text>
                      <Text style={[s.catSectionCount, { color: cat.color }]}>{catChecked}/{cat.items.length} · {catPct}%</Text>
                    </View>
                  </View>
                  <View style={s.catSectionList}>
                    {cat.items.map((item, i) => {
                      const isChecked = !!checkedItems[item];
                      return (
                        <TouchableOpacity key={i}
                          style={s.checkRow}
                          onPress={() => toggleCheck(item)}
                          activeOpacity={0.6}
                        >
                          <View style={[s.checkBox, isChecked && { backgroundColor: cat.color, borderColor: cat.color }]}>
                            {isChecked && <Text style={s.checkBoxTick}>✓</Text>}
                          </View>
                          <Text style={[s.checkText, isChecked && s.checkTextDone]}>
                            {item}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              );
            })}

            <View style={{ height: 80 }} />
          </>
          );
        })()}
      </ScrollView>
      {/* Modal dressing — choisir un article à emporter */}
      {showWardrobe && (
        <Modal visible animationType="slide" presentationStyle="overFullScreen">
          <SafeAreaView style={{ flex:1, backgroundColor:'#fff' }}>
            {/* Header */}
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:16, borderBottomWidth:1, borderBottomColor:'#F0F0F0' }}>
              <TouchableOpacity onPress={() => setShowWardrobe(false)}>
                <Text style={{ fontSize:18, color:'#999' }}>✕</Text>
              </TouchableOpacity>
              <View style={{ alignItems:'center' }}>
                <Text style={{ fontSize:16, fontWeight:'600', color:'#111' }}>{t('travel_my_dressing')}</Text>
                {selectedItems.size > 0 && (
                  <Text style={{ fontSize:11, color:'#2E7D32', marginTop:2 }}>
                    {t('travel_n_selected').replace('{n}', selectedItems.size).replace('{s}', selectedItems.size > 1 ? 's' : '')}
                  </Text>
                )}
              </View>
              <TouchableOpacity
                onPress={confirmAddSelected}
                style={{ backgroundColor: selectedItems.size > 0 ? '#111' : '#E0E0E0',
                  paddingHorizontal:14, paddingVertical:7, borderRadius:20 }}>
                <Text style={{ fontSize:12, fontWeight:'600', color: selectedItems.size > 0 ? '#fff' : '#999' }}>
                  {t('travel_add_n')} {selectedItems.size > 0 ? `(${selectedItems.size})` : ''}
                </Text>
              </TouchableOpacity>
            </View>

            {wardrobe.length === 0 ? (
              <View style={{ flex:1, alignItems:'center', justifyContent:'center', gap:12 }}>
                <Text style={{ fontSize:40 }}>👗</Text>
                <Text style={{ fontSize:16, color:'#555' }}>{t('travel_dressing_empty')}</Text>
                <Text style={{ fontSize:13, color:'#999', textAlign:'center', paddingHorizontal:32 }}>
                  {t('travel_dressing_empty_desc')}
                </Text>
              </View>
            ) : (
              <>
                {/* Bandeau limite dressing voyage — masque pour les unlimited (v1.12.142) */}
                {showTravelUpgradeBanner && !isTravelUnlimited && (
                  <View style={{
                    flexDirection:'row', alignItems:'center', gap:10,
                    marginHorizontal:16, marginTop:10,
                    backgroundColor:'#FFF9E6',
                    borderWidth:1, borderColor:'#F5DEB3',
                    borderRadius:10, padding:12,
                  }}>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:11, fontWeight:'700', color:'#B8860B', letterSpacing:0.5, marginBottom:2 }}>{t('travel_dressing_limited_badge')}</Text>
                      <Text style={{ fontSize:11, color:'#8B6F2E', lineHeight:15 }}>
                        {t('travel_dressing_limited_msg').replace('{total}', totalTravelWardrobeSize).replace('{max}', maxPacked)}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={{ paddingHorizontal:12, paddingVertical:8, backgroundColor:'#1A1A1A', borderRadius:6, borderWidth:1, borderColor:'#D4AF72' }}
                      onPress={() => {
                        setShowWardrobe(false);
                        setTimeout(() => travelGuard('wardrobe', totalTravelWardrobeSize), 300);
                      }}
                      activeOpacity={0.85}
                    >
                      <Text style={{ fontSize:10, fontWeight:'700', color:'#D4AF72', letterSpacing:0.5 }}>{t('travel_upgrade_short')}</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {/* v1.12.142 — Barre de recherche par nom/pseudo */}
                <View style={{ paddingHorizontal:16, paddingTop:10 }}>
                  <View style={{
                    flexDirection:'row', alignItems:'center', gap:8,
                    backgroundColor:'#F5F5F5', borderRadius:10, paddingHorizontal:12, paddingVertical:8,
                  }}>
                    <Text style={{ fontSize:14, color:'#999' }}>🔍</Text>
                    <TextInput
                      value={wardrobeSearch}
                      onChangeText={setWardrobeSearch}
                      placeholder={t('travel_search_placeholder')}
                      placeholderTextColor="#999"
                      style={{ flex:1, fontSize:13, color:'#111', padding:0 }}
                      returnKeyType="search"
                    />
                    {wardrobeSearch.length > 0 && (
                      <TouchableOpacity onPress={() => setWardrobeSearch('')} hitSlop={{top:8,bottom:8,left:8,right:8}}>
                        <Text style={{ fontSize:14, color:'#999' }}>✕</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>

                {/* v1.12.142 — Filtres categorie (gender-aware) */}
                {(() => {
                  const isMale = (user?.gender || 'F') === 'M';
                  const FEMALE_CATS = ['Tout', 'Robes', 'Hauts', 'Vestes', 'Bas', 'Chaussures', 'Sacs', 'Accessoires', 'Bijoux'];
                  const MALE_CATS   = ['Tout', 'Hauts', 'Vestes', 'Bas', 'Chaussures', 'Sacs', 'Accessoires'];
                  const cats = isMale ? MALE_CATS : FEMALE_CATS;
                  return (
                    <View style={{ paddingTop:10 }}>
                      <ScrollView horizontal showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ paddingHorizontal:16, gap:8 }}>
                        {cats.map(cat => {
                          const active = wardrobeFilterCat === cat;
                          // compteur par categorie (hors deja embarques)
                          const count = cat === 'Tout'
                            ? wardrobe.length
                            : wardrobe.filter(w => w.category === cat).length;
                          return (
                            <TouchableOpacity key={cat}
                              onPress={() => setWardrobeFilterCat(cat)}
                              activeOpacity={0.75}
                              style={{
                                paddingHorizontal:12, paddingVertical:7, borderRadius:18,
                                borderWidth:1.5,
                                backgroundColor: active ? '#111' : '#FFF',
                                borderColor: active ? '#111' : '#E5E5E5',
                              }}>
                              <Text style={{ fontSize:11, fontWeight:'600', letterSpacing:0.3,
                                color: active ? '#FFF' : '#444' }}>
                                {(() => { const M={'Tout':'travel_filter_all','Robes':'cat_dresses','Hauts':'cat_tops','Vestes':'cat_jackets','Bas':'cat_bottoms','Chaussures':'cat_shoes','Sacs':'cat_bags','Accessoires':'cat_accessories','Bijoux':'cat_jewelry'}; return M[cat] ? t(M[cat]) : cat; })()} {count > 0 ? `· ${count}` : ''}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </View>
                  );
                })()}

                {/* Sélectionner tout / Désélectionner — porte sur les items VISIBLES (filtre + search) */}
                {(() => {
                  // Filtrage applique pour le bouton "tout selectionner"
                  const q = wardrobeSearch.trim().toLowerCase();
                  const filtered = wardrobe.filter(it => {
                    if (wardrobeFilterCat !== 'Tout' && it.category !== wardrobeFilterCat) return false;
                    if (!q) return true;
                    const name = (it.name || '').toLowerCase();
                    const pseudo = (it.pseudo || '').toLowerCase();
                    return name.includes(q) || pseudo.includes(q);
                  });
                  const unpacked = filtered.filter(w => !packed.find(p => p.id === w.id));
                  const allSelected = unpacked.length > 0 && unpacked.every(w => selectedItems.has(w.id));
                  return (
                    <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:16, paddingVertical:8 }}>
                      <Text style={{ fontSize:11, color:'#999' }}>
                        {t('travel_n_articles').replace('{n}', filtered.length).replace('{s}', filtered.length > 1 ? 's' : '')}
                      </Text>
                      {unpacked.length > 0 && (
                        <TouchableOpacity onPress={() => {
                          if (allSelected) {
                            // deselectionner uniquement ceux visibles
                            setSelectedItems(prev => {
                              const next = new Set(prev);
                              for (const w of unpacked) next.delete(w.id);
                              return next;
                            });
                          } else {
                            setSelectedItems(prev => {
                              const next = new Set(prev);
                              for (const w of unpacked) next.add(w.id);
                              return next;
                            });
                          }
                        }}>
                          <Text style={{ fontSize:12, color:'#666', textDecorationLine:'underline' }}>
                            {allSelected ? t('travel_deselect_all') : t('travel_select_all')}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })()}

                <ScrollView contentContainerStyle={{ padding:12, flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                  {(() => {
                    const q = wardrobeSearch.trim().toLowerCase();
                    return wardrobe.filter(it => {
                      if (wardrobeFilterCat !== 'Tout' && it.category !== wardrobeFilterCat) return false;
                      if (!q) return true;
                      const name = (it.name || '').toLowerCase();
                      const pseudo = (it.pseudo || '').toLowerCase();
                      return name.includes(q) || pseudo.includes(q);
                    });
                  })().map(item => {
                    const isPacked   = !!packed.find(p => p.id === item.id);
                    const isSelected = selectedItems.has(item.id);
                    // v1.12.119 — items plus compacts : 4 col web / 3 col natif
                    // + ratio 1:1 (square) au lieu de hauteur fixe 120px qui
                    // étirait les images sur web et croppait fortement.
                    const colWidth = Platform.OS === 'web' ? '23%' : '31%';
                    return (
                      <TouchableOpacity key={`ti_${item.id || item.name || Math.random()}`}
                        onPress={() => !isPacked && toggleSelect(item)}
                        disabled={isPacked}
                        activeOpacity={0.8}
                        style={{ width: colWidth, borderWidth: 2,
                          borderColor: isPacked ? '#4CAF50' : isSelected ? '#111' : '#F0F0F0',
                          borderRadius: 8, overflow: 'hidden',
                          opacity: isPacked ? 0.5 : 1,
                          backgroundColor: isSelected ? '#F5F5F5' : '#fff' }}>
                        <View style={{ aspectRatio: 1, backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center', position:'relative' }}>
                          {item.image || item.image_url
                            ? <Image source={{ uri: item.image || item.image_url }} style={{ width:'100%', height:'100%', resizeMode:'cover' }} />
                            : <Text style={{ fontSize:24 }}>{item.emoji || '👗'}</Text>}
                          {/* Badge état */}
                          {isPacked && (
                            <View style={{ position:'absolute', top:4, right:4, backgroundColor:'#4CAF50', borderRadius:9, width:18, height:18, alignItems:'center', justifyContent:'center' }}>
                              <Text style={{ fontSize:10, color:'#fff' }}>✓</Text>
                            </View>
                          )}
                          {isSelected && !isPacked && (
                            <View style={{ position:'absolute', top:4, right:4, backgroundColor:'#111', borderRadius:9, width:18, height:18, alignItems:'center', justifyContent:'center' }}>
                              <Text style={{ fontSize:10, color:'#fff', fontWeight:'700' }}>✓</Text>
                            </View>
                          )}
                          {!isPacked && !isSelected && (
                            <View style={{ position:'absolute', top:4, right:4, borderWidth:1.5, borderColor:'#ddd', backgroundColor:'#fff', borderRadius:9, width:18, height:18 }} />
                          )}
                        </View>
                        <View style={{ padding:6 }}>
                          <Text style={{ fontSize:10, fontWeight:'500', color:'#111' }} numberOfLines={1}>{item.name}</Text>
                          <Text style={{ fontSize:9, color:'#999' }} numberOfLines={1}>{item.category}{item.color ? ` · ${item.color}` : ''}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>

                {/* Footer bouton confirmer */}
                {selectedItems.size > 0 && (
                  <View style={{ padding:16, borderTopWidth:1, borderTopColor:'#F0F0F0' }}>
                    <TouchableOpacity
                      onPress={confirmAddSelected}
                      style={{ backgroundColor:'#111', paddingVertical:14, borderRadius:10, alignItems:'center' }}>
                      <Text style={{ fontSize:14, fontWeight:'600', color:'#fff' }}>
                        {t('travel_add_n_to_bag').replace('{n}', selectedItems.size).replace('{s}', selectedItems.size > 1 ? 's' : '')}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            )}
          </SafeAreaView>
        </Modal>
      )}

      {/* Modal détail produit partenaire (identique au Catalogue) */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          userId={user?.id || null}
          screen="travel"
          onClose={() => setSelectedProduct(null)}
          onBought={(p) => {
            setShowWardrobe(false);
            setBoughtProduct(p || selectedProduct);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* v1.12.140 — Detail d'un article du dressing (clic depuis un look voyage) */}
      <WardrobeItemDetailModal
        visible={!!selectedWardrobeItem}
        item={selectedWardrobeItem}
        onClose={() => setSelectedWardrobeItem(null)}
      />

      {boughtProduct && (
        <PurchaseConfirmModal
          product={boughtProduct}
          onClose={() => setBoughtProduct(null)}
          onConfirmed={(item) => {
            setBoughtProduct(null);
            DeviceEventEmitter.emit('wardrobe_updated', { newItem: item });
            // Recharger le dressing pour voir le nouvel article (avec limite tier)
            loadWardrobe();
          }}
        />
      )}

      {/* ═══ v1.10.26 : Modal d'essayage virtuel sur mannequin (looks voyage) ═══ */}
      <TryOnModal
        visible={!!tryOnOutfit}
        onClose={() => { setTryOnOutfit(null); setTryOnAuthToken(null); }}
        outfit={tryOnOutfit}
        userGender={user?.gender || 'F'}
        authToken={tryOnAuthToken}
        onUpgradePress={() => {/* navigation profile gérée plus haut si besoin */}}
        onProductPress={(product) => setSelectedProduct(product)}
      />
    </SafeAreaView>
      <UpgradeModal
        visible={showTravelPremium}
        onClose={closeTravelPremium}
        currentTier={limitInfo?.tier || 'free'}
        feature={limitInfo?.trigger || 'ai'}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit } : null}
        onSubscribe={async (planId) => {
          const { activateSubscription } = require('../services/TestSubscribeService');
          await activateSubscription(planId, user, null);
        }}
      />
    </>
  );
}

/* ══ Nouveau voyage ═════════════════════════════════════════ */
function NewTripForm({ data, onChange, onSubmit, onCancel, isEditing, t }) {
  // ═══ Mode sombre ═══
  // s = styles principaux (topbar, safe, etc.) recréés à partir du même makeStyles
  // que l'écran parent — sinon les `s.xxx` (s.safe, s.topbar, s.backNavBtn...)
  // étaient undefined et le formulaire s'affichait sans aucun style → écran blanc.
  const { dark, theme } = useTheme();
  const s  = useMemo(() => makeStyles(theme, dark),     [theme, dark]);
  const sf = useMemo(() => makeFormStyles(theme, dark), [theme, dark]);
  // ═══ FIX 2026-04 : safe-area Android (cf. composant parent) ═══
  const insets = useSafeAreaInsets();
  const topPadding = Platform.OS === 'android'
    ? Math.max(insets.top, StatusBar.currentHeight || 24)
    : Math.max(insets.top, 0);
  const [destQuery,      setDestQuery]      = useState(data.destination || '');
  const [destResults,    setDestResults]    = useState([]);
  const [fetchingTemp,   setFetchingTemp]   = useState(false);
  const [tempDisplay,    setTempDisplay]    = useState(
    data.estimatedTemp ? `${data.estimatedTemp}°C estim\u00e9` : ''
  );
  const [showDepCal,     setShowDepCal]     = useState(false);
  const [showRetCal,     setShowRetCal]     = useState(false);
  const debounceRef = useRef(null);

  // ── Autocomplete villes ──────────────────────────────────
  const searchCities = useCallback((query) => {
    if (!query || query.length < 2) { setDestResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=8&language=fr&format=json`
        );
        const json = await res.json();
        setDestResults(json.results || []);
      } catch(e) {}
    }, 350);
  }, []);

  // ── Calculer température ─────────────────────────────────
  const fetchTemperature = useCallback(async (lat, lon, dateStr) => {
    if (!lat || !lon || !dateStr) { setTempDisplay('Pending'); return; }
    setFetchingTemp(true);
    setTempDisplay('...');
    try {
      const today    = new Date();
      const tripDate = new Date(dateStr);
      const diffDays = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
      let tempC      = null;
      let weatherKey = 'temperate';

      if (diffDays >= 0 && diffDays <= 16) {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum` +
          `&timezone=auto&start_date=${dateStr}&end_date=${dateStr}`
        );
        if (res.ok) {
          const json   = await res.json();
          const tmax   = json.daily?.temperature_2m_max?.[0];
          const tmin   = json.daily?.temperature_2m_min?.[0];
          const precip = json.daily?.precipitation_sum?.[0] || 0;
          if (tmax != null && tmin != null) {
            tempC = Math.round((tmax + tmin) / 2);
            if (precip > 5)       weatherKey = 'rainy';
            else if (tempC >= 30) weatherKey = 'hot_dry';
            else if (tempC >= 25) weatherKey = 'hot_humid';
            else if (tempC >= 15) weatherKey = 'temperate';
            else if (tempC >= 5)  weatherKey = 'cold';
            else                  weatherKey = 'very_cold';
          }
        }
      } else {
        // Données climatiques actuelles (voyage futur > 16j)
        const res = await fetch(`https://wttr.in/${lat},${lon}?format=j1`);
        if (res.ok) {
          const json = await res.json();
          const avg  = json?.weather?.[0];
          if (avg) {
            tempC = Math.round((parseInt(avg.maxtempC) + parseInt(avg.mintempC)) / 2);
            weatherKey = tempC >= 30 ? 'hot_dry' : tempC >= 25 ? 'hot_humid' :
                         tempC >= 15 ? 'temperate' : tempC >= 5 ? 'cold' : 'very_cold';
          }
        }
      }

      if (tempC != null) {
        setTempDisplay(`${tempC}°C estim\u00e9`);
        // ═══ FIX : utiliser updater fonctionnel pour éviter écraser les champs
        //         ajoutés entre temps (ex: departure en cours de confirmation) ═══
        // Avant, `onChange({ ...data, ... })` écrasait data avec sa version capturée
        // au moment du call → si l'user avait entre temps sélectionné une date de
        // départ, elle était perdue. Le functional updater `prev => ({ ...prev, ... })`
        // garantit qu'on merge avec la version la plus récente.
        onChange(prev => ({ ...prev, weather: weatherKey, estimatedTemp: tempC, destLat: lat, destLon: lon }));
      } else {
        setTempDisplay('Pending');
      }
    } catch(e) { setTempDisplay('Pending'); }
    setFetchingTemp(false);
  }, [onChange]); // on retire `data` des deps : plus besoin puisqu'on utilise l'updater fonctionnel

  // ── Sélectionner une ville ───────────────────────────────
  const selectCity = useCallback((city) => {
    // ═══ Fermer le clavier dès qu'une destination est sélectionnée ═══
    // L'user vient de finir sa saisie de ville, le clavier n'a plus de raison
    // d'être affiché → on libère l'écran pour qu'il voie le formulaire complet.
    Keyboard.dismiss();
    const label = `${city.name}, ${city.country}`;
    setDestQuery(label);
    setDestResults([]);
    onChange(prev => {
      const updated = { ...prev, destination: label, destLat: city.latitude, destLon: city.longitude };
      // fetch temp de façon async si on a déjà une date de départ
      if (updated.departure) fetchTemperature(city.latitude, city.longitude, updated.departure);
      return updated;
    });
  }, [onChange, fetchTemperature]);

  // ── Confirmer date de départ ─────────────────────────────
  const confirmDeparture = useCallback((dateStr) => {
    // ═══ Fermer le clavier (au cas où il était encore ouvert) ═══
    Keyboard.dismiss();
    // 1. Fermer le modal immédiatement
    setShowDepCal(false);
    // 2. Différer l'update + fetch météo pour ne pas freeze l'animation
    setTimeout(() => {
      onChange(prev => {
        let days = prev.days || 5;
        if (prev.returnDate && dateStr) {
          const dep = new Date(dateStr);
          const ret = new Date(prev.returnDate);
          if (ret > dep) days = Math.ceil((ret - dep) / (1000 * 60 * 60 * 24));
        }
        const updated = { ...prev, departure: dateStr, days };
        // Fetch temp si on a la position (async, pas bloquant)
        if (updated.destLat && updated.destLon) fetchTemperature(updated.destLat, updated.destLon, dateStr);
        return updated;
      });
    }, 50);
  }, [onChange, fetchTemperature]);

  // ── Confirmer date de retour ─────────────────────────────
  const confirmReturn = useCallback((dateStr) => {
    // ═══ Fermer le clavier (au cas où il était encore ouvert) ═══
    Keyboard.dismiss();
    // 1. Fermer le modal immédiatement (priorité UX)
    setShowRetCal(false);
    // 2. Différer le lourd calcul + update dans un microtick
    //    → laisse l'animation de fermeture se terminer sans freeze
    setTimeout(() => {
      onChange(prev => {
        let days = prev.days || 5;
        if (prev.departure && dateStr) {
          const dep = new Date(prev.departure);
          const ret = new Date(dateStr);
          if (ret > dep) days = Math.ceil((ret - dep) / (1000 * 60 * 60 * 24));
        }
        return { ...prev, returnDate: dateStr, days };
      });
    }, 50);
  }, [onChange]);

  // ── Format date lisible ──────────────────────────────────
  const MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
  const fmtDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-');
    return `${parseInt(d)} ${MONTHS_SHORT[parseInt(m)-1]} ${y}`;
  };

  return (
    <>
    <SafeAreaView style={s.safe}>
      <View style={[s.topbar, { paddingTop: topPadding + 8 }]}>
        <BackButton onPress={onCancel} />
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={s.topbarBadge}>{isEditing ? t('travel_form_badge_edit') : t('travel_form_badge_new')}</Text>
          <Text style={s.titlePremium}>{isEditing ? t('travel_form_title_edit') : t('travel_form_title_new')}</Text>
        </View>
        <View style={{ width: 32 }} />
      </View>
      <Divider />

      <ScrollView showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingHorizontal: spacing.xl, paddingBottom: 40 }}>

        {/* ═══ Étape 1 : Nom du voyage ═══ */}
        <View style={sf.stepRow}>
          <View style={sf.stepBadge}><Text style={sf.stepBadgeText}>1</Text></View>
          <Text style={sf.stepTitle}>{t('travel_form_step_name')}</Text>
        </View>
        <TextInput style={sf.inputPremium} value={data.name}
          onChangeText={v => onChange({ ...data, name: v })}
          placeholder={t('travel_form_name_placeholder')}
          placeholderTextColor={theme.textFaint} />

        {/* ═══ Étape 2 : Destination ═══ */}
        <View style={sf.stepRow}>
          <View style={sf.stepBadge}><Text style={sf.stepBadgeText}>2</Text></View>
          <Text style={sf.stepTitle}>{t('destination')}</Text>
        </View>
        <View style={{ zIndex: 200 }}>
          <View style={sf.searchInputWrap}>
            <Text style={sf.searchInputIcon}>📍</Text>
            <TextInput style={sf.searchInput}
              value={destQuery}
              onChangeText={v => {
                setDestQuery(v);
                onChange({ ...data, destination: v, destLat: null, destLon: null });
                searchCities(v);
              }}
              placeholder={t('travel_form_dest_placeholder')}
              placeholderTextColor={theme.textFaint} />
          </View>
          {destResults.length > 0 && (
            <View style={sf.autocomplete}>
              {destResults.map((city, i) => (
                <TouchableOpacity key={i} style={sf.autocompleteRow}
                  onPress={() => selectCity(city)} activeOpacity={0.7}>
                  <Text style={sf.autocompleteFlag}>📍</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={sf.autocompleteCity}>{city.name}</Text>
                    <Text style={sf.autocompleteCountry}>
                      {city.admin1 ? `${city.admin1}, ` : ''}{city.country}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* ═══ Étape 3 : Dates ═══ */}
        <View style={sf.stepRow}>
          <View style={sf.stepBadge}><Text style={sf.stepBadgeText}>3</Text></View>
          <Text style={sf.stepTitle}>{t('travel_form_step_dates')}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 10, zIndex: 1 }}>
          <View style={{ flex: 1 }}>
            <Text style={sf.dateLabel}>{t('travel_form_label_departure')}</Text>
            <TouchableOpacity style={sf.dateBtnPremium} onPress={() => { Keyboard.dismiss(); setShowDepCal(true); }} activeOpacity={0.85}>
              <View style={{ flex: 1 }}>
                {data.departure
                  ? <Text style={sf.dateBtnValue}>{fmtDate(data.departure)}</Text>
                  : <Text style={sf.dateBtnPlaceholder}>{t('travel_form_pick_date')}</Text>}
              </View>
              <Text style={{ fontSize: 18 }}>📅</Text>
            </TouchableOpacity>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={sf.dateLabel}>{t('travel_form_label_return')}</Text>
            <TouchableOpacity style={sf.dateBtnPremium} onPress={() => { Keyboard.dismiss(); setShowRetCal(true); }} activeOpacity={0.85}>
              <View style={{ flex: 1 }}>
                {data.returnDate
                  ? <Text style={sf.dateBtnValue}>{fmtDate(data.returnDate)}</Text>
                  : <Text style={sf.dateBtnPlaceholder}>{t('travel_form_pick_date')}</Text>}
              </View>
              <Text style={{ fontSize: 18 }}>📅</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ═══ Auto-info : Durée + Température ═══ */}
        <View style={sf.autoInfoRow}>
          <View style={sf.autoInfoCard}>
            <Text style={sf.autoInfoIcon}>🗓</Text>
            <View style={{ flex: 1 }}>
              <Text style={sf.autoInfoVal}>{data.days ? t('travel_form_n_days').replace('{n}', data.days).replace('{s}', (data.days || 0) > 1 ? 's' : '') : '—'}</Text>
              <Text style={sf.autoInfoLbl}>{t('travel_form_duration')}</Text>
            </View>
          </View>
          <TouchableOpacity style={sf.autoInfoCard}
            onPress={() => {
              if (data.destLat && data.destLon && data.departure)
                fetchTemperature(data.destLat, data.destLon, data.departure);
            }}
            activeOpacity={0.85}>
            <Text style={sf.autoInfoIcon}>🌡</Text>
            <View style={{ flex: 1 }}>
              {fetchingTemp
                ? <ActivityIndicator size="small" color={theme.textFaint} />
                : <Text style={sf.autoInfoVal}>
                    {tempDisplay || (data.departure && data.destLat ? '...' : '—')}
                  </Text>}
              <Text style={sf.autoInfoLbl}>
                {fetchingTemp ? t('travel_form_calculating') : t('travel_form_estimated_temp')}
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* ═══ Étape 4 : Style(s) de voyage — multi-sélection ═══ */}
        <View style={sf.stepRow}>
          <View style={sf.stepBadge}><Text style={sf.stepBadgeText}>4</Text></View>
          <Text style={sf.stepTitle}>{t('travel_form_step_styles')}</Text>
        </View>
        <Text style={sf.stepSub}>{t('travel_form_step_styles_sub')}</Text>
        <View style={sf.styleGridPremium}>
          {TRIP_STYLES.map(style => {
            // data.styles est un array. Support rétrocompatible si ancien data.style (string)
            const selectedStyles = Array.isArray(data.styles)
              ? data.styles
              : (data.style ? [data.style] : []);
            const isSelected = selectedStyles.includes(style.id);
            return (
              <TouchableOpacity key={style.id}
                style={[
                  sf.styleChipPremium,
                  isSelected && sf.styleChipPremiumActive,
                ]}
                onPress={() => {
                  // Toggle : ajoute ou retire ce style de la liste
                  const next = isSelected
                    ? selectedStyles.filter(s => s !== style.id)
                    : [...selectedStyles, style.id];
                  // Garder au moins un style sélectionné
                  if (next.length === 0) return;
                  onChange({ ...data, styles: next, style: next[0] /* compat ancien champ */ });
                }}
                activeOpacity={0.85}>
                <Text style={sf.styleChipPremiumIcon}>{style.icon}</Text>
                <Text style={[
                  sf.styleChipPremiumLabel,
                  isSelected && sf.styleChipPremiumLabelActive,
                ]}>
                  {style.labelKey ? t(style.labelKey) : style.label}
                </Text>
                {isSelected && (
                  <View style={sf.styleChipPremiumCheck}>
                    <Text style={sf.styleChipPremiumCheckText}>✓</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* ═══ CTA de création ═══ */}
        <TouchableOpacity
          style={[
            sf.createBtn,
            (!data.name || !data.destination) && sf.createBtnDisabled,
          ]}
          onPress={onSubmit}
          activeOpacity={0.85}
          disabled={!data.name || !data.destination}
        >
          <Text style={sf.createBtnText}>{isEditing ? t('travel_form_save') : t('travel_form_create')}</Text>
          <Text style={sf.createBtnArrow}>{isEditing ? '✓' : '→'}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onCancel}
          style={{ paddingVertical: spacing.md, alignItems: 'center' }}>
          <Text style={{ fontSize: 11, letterSpacing: 1, color: theme.textFaint }}>
            {t('cancel')}
          </Text>
        </TouchableOpacity>
      </ScrollView>

      {/* Calendriers */}
      <DatePickerModal
        visible={showDepCal}
        value={data.departure || null}
        title={t('travel_date_departure_title')}
        onConfirm={confirmDeparture}
        onCancel={() => setShowDepCal(false)}
      />
      <DatePickerModal
        visible={showRetCal}
        value={data.returnDate || null}
        minDate={data.departure || null}
        title={t('travel_date_return_title')}
        onConfirm={confirmReturn}
        onCancel={() => setShowRetCal(false)}
      />
    </SafeAreaView>
    </>
  );
}

const makeFormStyles = (theme, dark = false) => StyleSheet.create({
  // ═══ Ancien autocomplete conservé ═══
  autocomplete:        { position: 'absolute', top: 54, left: 0, right: 0, backgroundColor: theme.bgCard, borderRadius: radius.md, borderWidth: 1, borderColor: theme.bgBorder, zIndex: 300, elevation: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: dark ? 0.4 : 0.12, shadowRadius: 8, maxHeight: 280 },
  autocompleteRow:     { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.bgSubtle },
  autocompleteFlag:    { fontSize: 16 },
  autocompleteCity:    { fontSize: 14, fontWeight: '600', color: theme.text },
  autocompleteCountry: { fontSize: 11, color: theme.textFaint, marginTop: 2 },
  dateBtn:             { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: theme.bgCard, borderRadius: 8, borderWidth: 0.5, borderColor: theme.bgBorder, paddingHorizontal: 12, paddingVertical: 14, minHeight: 50 },
  dateBtnValue:        { fontSize: 13, fontWeight: '600', color: theme.text },
  dateBtnPlaceholder:  { fontSize: 13, color: theme.textFaint },
  infoChip:            { flex: 1, backgroundColor: theme.bgSubtle, borderRadius: 12, paddingVertical: 14, paddingHorizontal: 12, alignItems: 'center', minHeight: 72 },
  infoChipVal:         { fontSize: 18, fontWeight: '400', color: theme.text },
  infoChipLabel:       { fontSize: 9, letterSpacing: 1, color: theme.textFaint, textTransform: 'uppercase', marginTop: 4, textAlign: 'center' },

  // ═══════════ NOUVEAU FORM PREMIUM ═══════════
  stepRow:            { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: spacing.xl, marginBottom: spacing.sm },
  stepBadge:          { width: 26, height: 26, borderRadius: 13, backgroundColor: '#D4AF37', alignItems: 'center', justifyContent: 'center' },
  stepBadgeText:      { fontSize: 12, fontWeight: '700', color: dark ? '#1A1814' : theme.bgCard },
  stepTitle:          { fontSize: 15, fontWeight: '600', color: theme.text, letterSpacing: 0.2 },
  stepSub:            { fontSize: 11, color: theme.textFaint, marginBottom: spacing.md, marginLeft: 36 },

  inputPremium:       { backgroundColor: theme.bgSurface, borderWidth: 1, borderColor: theme.bgBorder, borderRadius: radius.md, paddingHorizontal: 14, paddingVertical: 14, fontSize: 14, color: theme.text, marginBottom: spacing.sm },

  searchInputWrap:    { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.bgSurface, borderWidth: 1, borderColor: theme.bgBorder, borderRadius: radius.md, paddingHorizontal: 14, paddingVertical: 2 },
  searchInputIcon:    { fontSize: 16, marginRight: 8 },
  searchInput:        { flex: 1, paddingVertical: 12, fontSize: 14, color: theme.text },

  dateLabel:          { fontSize: 9, letterSpacing: 2, color: theme.textFaint, textTransform: 'uppercase', marginBottom: 6, marginTop: 8 },
  dateBtnPremium:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: theme.bgSurface, borderRadius: radius.md, borderWidth: 1, borderColor: theme.bgBorder, paddingHorizontal: 12, paddingVertical: 14, minHeight: 52 },

  autoInfoRow:        { flexDirection: 'row', gap: 10, marginTop: spacing.lg, marginBottom: spacing.md },
  // Cards "auto-info" (température/durée) : adaptées au mode sombre
  autoInfoCard:       { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: dark ? theme.bgSurface : '#FFF9E6', borderRadius: radius.md, borderWidth: 1, borderColor: dark ? '#3A2F1A' : '#F5DEB3', paddingHorizontal: 12, paddingVertical: 12, minHeight: 64 },
  autoInfoIcon:       { fontSize: 22 },
  autoInfoVal:        { fontSize: 14, fontWeight: '700', color: theme.text },
  autoInfoLbl:        { fontSize: 9, color: theme.textFaint, marginTop: 2, letterSpacing: 0.3 },

  styleGridPremium:   { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: spacing.sm },
  styleChipPremium:   { width: '48.5%', backgroundColor: theme.bgCard, borderRadius: radius.md, borderWidth: 1, borderColor: theme.bgBorder, paddingVertical: spacing.md, paddingHorizontal: spacing.sm, alignItems: 'center', position: 'relative', minHeight: 80 },
  // Chip actif : doré sur fond crème (clair) ou doré sur surface sombre teintée (sombre)
  styleChipPremiumActive:{ backgroundColor: dark ? '#2A2418' : '#FFF9E6', borderColor: '#D4AF37' },
  styleChipPremiumIcon:{ fontSize: 26, marginBottom: 4 },
  styleChipPremiumLabel:{ fontSize: 12, fontWeight: '500', color: theme.textSec, textAlign: 'center' },
  styleChipPremiumLabelActive:{ color: dark ? '#D4AF72' : '#B8860B', fontWeight: '700' },
  styleChipPremiumCheck:{ position: 'absolute', top: 6, right: 6, width: 18, height: 18, borderRadius: 9, backgroundColor: '#D4AF37', alignItems: 'center', justifyContent: 'center' },
  styleChipPremiumCheckText:{ fontSize: 10, color: dark ? '#1A1814' : theme.bgCard, fontWeight: '700' },

  createBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: spacing.xl, backgroundColor: theme.text, paddingVertical: 16, borderRadius: radius.pill },
  createBtnDisabled:  { opacity: 0.4 },
  createBtnText:      { fontSize: 14, fontWeight: '700', color: theme.bg, letterSpacing: 0.5 },
  createBtnArrow:     { fontSize: 18, color: '#FFD700', fontWeight: '700' },
});



function getStyleColor(style) {
  const map = { beach:'#1D9E75', city:'#534AB7', mountain:'#854F0B', business:'#A89E94', wedding:'#D4537E', backpacker:'#D85A30', luxury:'#B89050', safari:'#639922' };
  return map[style] || '#888';
}

// ═══ Factory de styles thématiques (light/dark) ═══
// theme = LIGHT ou DARK depuis ThemeContext
// Tous les hardcoded theme.bgCard, theme.bgSurface du fichier ont été remplacés par
// theme.bgCard / theme.bgSurface pour s'adapter automatiquement au mode sombre.
const makeStyles = (theme, dark = false) => StyleSheet.create({
  safe:          { flex:1, backgroundColor: theme.bg },
  topbar:        { flexDirection:'row', alignItems:'center', gap:10, paddingHorizontal:spacing.xl, paddingBottom:spacing.md },
  backBtn:       { width:28, alignItems:'center' },
  backText:      { fontSize:22, color:theme.textSec, lineHeight:26 },
  title:         { fontSize:13, letterSpacing:4, color:theme.textSec, textTransform:'uppercase', flex:1 },
  subtitle:      { fontSize:8, letterSpacing:2, color:theme.textFaint, marginTop:2 },
  addBtn:        { width:30, height:30, borderRadius:15, borderWidth:0.5, borderColor:theme.accent, alignItems:'center', justifyContent:'center' },
  addBtnText:    { fontSize:16, color:theme.accent, lineHeight:20 },

  banner:        { flexDirection:'row', gap:12, alignItems:'flex-start', marginHorizontal:spacing.xl, marginTop:spacing.md, marginBottom:spacing.lg, backgroundColor:theme.bgCard, borderRadius:radius.md, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md },
  bannerEmoji:   { fontSize:24 },
  bannerText:    { flex:1 },
  bannerTitle:   { fontSize:12, color:theme.accent, letterSpacing:0.5, fontWeight:'500', marginBottom:3 },
  bannerSub:     { fontSize:10, color:theme.textFaint, lineHeight:16 },

  section:       { paddingHorizontal:spacing.xl },
  sectionLabel:  { fontSize:8, letterSpacing:3, color:theme.textFaint, textTransform:'uppercase', marginBottom:spacing.sm },

  tripCard:      { flexDirection:'row', gap:10, alignItems:'center', backgroundColor:theme.bgCard, borderRadius:radius.md, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md, marginBottom:8 },
  tripCardLeft:  { alignItems:'center', gap:4 },
  tripFlag:      { fontSize:22 },
  styleDot:      { width:6, height:6, borderRadius:3 },
  tripInfo:      { flex:1 },
  tripName:      { fontSize:13, color:theme.textSec, letterSpacing:0.3, fontWeight:'400' },
  tripDest:      { fontSize:9, letterSpacing:1.5, color:theme.textFaint, textTransform:'uppercase', marginTop:2 },
  tripMeta:      { flexDirection:'row', gap:5, marginTop:5, flexWrap:'wrap' },
  metaPill:      { backgroundColor:theme.bgSurface, borderRadius:radius.pill, borderWidth:0.5, borderColor:theme.bgBorder, paddingHorizontal:7, paddingVertical:2 },
  metaText:      { fontSize:9, color:theme.textFaint },
  tripRight:     { alignItems:'center' },
  tripItems:     { fontSize:16, color:theme.text, fontWeight:'300' },
  tripItemsLabel:{ fontSize:8, letterSpacing:1.5, color:theme.textFaint, textTransform:'uppercase' },
  tripArrow:     { fontSize:16, color:theme.textFaint, marginTop:2 },

  tripDeleteBtn:   { padding:10, justifyContent:'center', alignItems:'center' },
  tripDeleteTxt:   { fontSize:16 },
  newTripBtn:    { borderWidth:0.5, borderColor:theme.textFaint, paddingVertical:11, alignItems:'center', marginTop:4 },
  newTripBtnText:{ fontSize:9, letterSpacing:3, color:theme.textSec, textTransform:'uppercase' },

  weatherBar:    { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:spacing.xl, paddingVertical:spacing.sm, backgroundColor:theme.bgCard, borderBottomWidth:0.5, borderBottomColor:theme.bgBorder },
  weatherLeft:   { flexDirection:'row', gap:8, alignItems:'center' },
  weatherIcon:   { fontSize:18 },
  weatherLabel:  { fontSize:11, color:theme.textSec, letterSpacing:0.3 },
  weatherTemp:   { fontSize:9, letterSpacing:1.5, color:theme.textFaint, textTransform:'uppercase', marginTop:1 },
  styleBadge:    { backgroundColor:theme.accentGhost, borderRadius:radius.pill, borderWidth:0.5, borderColor:theme.textFaint, paddingHorizontal:9, paddingVertical:3 },
  styleBadgeText:{ fontSize:9, color:theme.textSec, letterSpacing:1.5, textTransform:'uppercase' },

  tabs:          { flexDirection:'row', paddingHorizontal:spacing.xl },
  tab:           { flex:1, paddingVertical:10, alignItems:'center' },
  tabLabel:      { fontSize:8, letterSpacing:2, color:theme.textFaint, textTransform:'uppercase' },
  tabLabelActive:{ color:theme.accent },
  tabLine:       { height:0.5, backgroundColor:theme.bgBorder, marginHorizontal:spacing.xl, position:'relative', marginBottom:spacing.md },
  tabUnder:      { position:'absolute', top:-0.5, width:'33.3%', height:1, backgroundColor:theme.accent },

  tabHeader:     { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:spacing.md },
  missingSectionBox:{ backgroundColor:'#FFF8E1', borderRadius:10, padding:12, marginBottom:16, borderWidth:1, borderColor:'#FFE082' },
  missingSectionTitle:{ fontSize:12, fontWeight:'600', color:'#F57C00', marginBottom:8 },
  missingPillsRow:  { flexDirection:'row', flexWrap:'wrap', gap:6 },
  missingCatPill:   { paddingHorizontal:10, paddingVertical:4, backgroundColor:'#FFF3E0', borderRadius:20, borderWidth:1, borderColor:'#FFCC80' },
  missingCatPillText:{ fontSize:11, color:'#E65100', fontWeight:'500' },
  suggestSection:   { marginBottom:16 },
  suggestSectionTitle:{ fontSize:13, fontWeight:'600', color:'#111', marginBottom:8 },
  suggestCard:      { width:140, backgroundColor:'#fff', borderRadius:10, borderWidth:1, borderColor:'#EFEFEF', overflow:'hidden' },
  suggestCardImg:   { width:'100%', height:160, backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center', position:'relative' },
  suggestCardInfo:  { padding:8 },
  suggestCardCat:   { fontSize:9, letterSpacing:1.5, color:'#999', textTransform:'uppercase', marginBottom:2 },
  suggestCardName:  { fontSize:11, fontWeight:'500', color:'#111', lineHeight:14, marginBottom:4 },
  suggestCardPrice: { fontSize:13, fontWeight:'700', color:'#111' },
  partnerBadge:     { position:'absolute', top:6, right:6, width:20, height:20, borderRadius:10, alignItems:'center', justifyContent:'center' },
  partnerBadgeTxt:  { fontSize:7, color:'#fff', fontWeight:'700' },
  backNavBtn:    { paddingRight: 12, paddingVertical: 4 },
  backNavText:   { fontSize: 24, color: theme.text, fontWeight: '300' },
  tabTitle:      { fontSize:8, letterSpacing:3, color:theme.textFaint, textTransform:'uppercase' },
  addItemBtn:    { borderWidth:0.5, borderColor:theme.accent, paddingHorizontal:10, paddingVertical:5, borderRadius:radius.sm },
  addItemBtnText:{ fontSize:8, letterSpacing:2, color:theme.accent, textTransform:'uppercase' },

  emptyBag:      { paddingVertical:32, alignItems:'center' },
  emptyBagEmoji: { fontSize:32, marginBottom:spacing.md },
  emptyBagTitle: { fontSize:12, letterSpacing:3, color:theme.textSec, textTransform:'uppercase', marginBottom:6 },
  emptyBagText:  { fontSize:10, color:theme.textFaint, textAlign:'center', lineHeight:18, maxWidth:240 },

  packedGrid:     { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: spacing.md },
  packedItem:     { width: '31.5%', alignItems: 'center', backgroundColor: theme.bgCard, borderRadius: radius.md, borderWidth: 1, borderColor: theme.bgBorder, padding: 8, position: 'relative' },
  packedItemThumb:{ width: '100%', aspectRatio: 1, backgroundColor: '#F5F5F5', borderRadius: radius.sm, alignItems: 'center', justifyContent: 'center', marginBottom: 8, overflow: 'hidden', position: 'relative' },
  packedItemPhoto:{ width: '100%', height: '100%', resizeMode: 'cover' },
  packedItemEmoji:{ fontSize: 36 },
  packedItemName: { fontSize: 10, color: theme.text, fontWeight: '600', textAlign: 'center', letterSpacing: 0.2 },
  packedItemColor:{ fontSize: 9, color: theme.textFaint, textAlign: 'center', marginTop: 2 },
  packedItemDel:  { position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 11, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center' },
  packedItemDelTxt:{ color: theme.bgCard, fontSize: 11, fontWeight: '600' },

  weatherAdvice: { flexDirection:'row', gap:8, alignItems:'flex-start', backgroundColor:theme.bgCard, borderRadius:radius.sm, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md, marginTop:spacing.md },
  weatherAdviceIcon:{ fontSize:16 },
  weatherAdviceText:{ flex:1, fontSize:11, color:theme.textFaint, lineHeight:18, fontStyle:'italic' },

  lookCard:      { backgroundColor:theme.bgCard, borderRadius:radius.md, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md, marginBottom:10 },
  lookItems:     { flexDirection:'row', gap:6, marginBottom:spacing.sm },
  lookItem:      { width:40, height:48, backgroundColor:theme.bgSurface, borderRadius:8, alignItems:'center', justifyContent:'center', borderWidth:0.5, borderColor:theme.bgBorder },
  lookItemEmoji: { fontSize:18 },
  lookInfo:      {},
  lookInfoTop:   { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:6 },
  lookName:      { fontSize:12, color:theme.textSec, letterSpacing:0.3, fontWeight:'400' },
  occasionBadge: { backgroundColor:theme.accentGhost, borderRadius:radius.pill, borderWidth:0.5, borderColor:theme.textFaint, paddingHorizontal:7, paddingVertical:2 },
  occasionBadgeText:{ fontSize:8, color:theme.textSec, letterSpacing:1.5, textTransform:'uppercase' },
  lookScore:     { flexDirection:'row', alignItems:'center', gap:8, marginBottom:6 },
  lookScorePct:  { fontSize:11, color:theme.accent },
  catalogSuggestSection: { paddingHorizontal: 14, paddingTop: 10, paddingBottom: 4, borderTopWidth: 0.5, borderTopColor: theme.bgSubtle },
  catalogSuggestTitle:   { fontSize: 11, fontWeight: '600', color: '#9B7B5A', letterSpacing: 0.5, marginBottom: 6 },
  missingTravelBlock:    { marginHorizontal: spacing.md, marginBottom: spacing.sm, padding: spacing.sm, backgroundColor: theme.bgSurface, borderRadius: radius.md, borderWidth: 1, borderColor: '#EEE' },
  missingTravelTitle:    { fontSize: 12, fontWeight: '700', color: theme.text, marginBottom: 8 },
  missingTravelPill:     { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 7, borderRadius: radius.md, borderWidth: 1, gap: 10, marginBottom: 5 },
  missingTravelEmoji:    { fontSize: 16 },
  missingTravelCat:      { fontSize: 12, fontWeight: '700' },
  missingTravelReason:   { fontSize: 10, marginTop: 1 },
  catalogSuggestCard:    { width: 100, backgroundColor: theme.bgSurface, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: theme.bgSubtle },
  catalogSuggestImg:     { width: 100, height: 100, backgroundColor: theme.bgRaised, position: 'relative' },
  catalogSuggestBadge:   { position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  catalogSuggestName:    { fontSize: 10, color: '#333', paddingHorizontal: 6, paddingTop: 5, lineHeight: 13 },
  catalogSuggestPrice:   { fontSize: 11, fontWeight: '600', color: '#9B7B5A', paddingHorizontal: 6, paddingBottom: 6 },
  lookTip:       { fontSize:10, color:theme.textFaint, fontStyle:'italic', lineHeight:16 },
  missingSection:{ marginTop:spacing.sm, paddingTop:spacing.sm, borderTopWidth:0.5, borderTopColor:theme.bgBorder },
  missingLabel:  { fontSize:8, letterSpacing:2, color:theme.textFaint, textTransform:'uppercase', marginBottom:6 },
  missingItem:   { flexDirection:'row', alignItems:'center', gap:8, marginBottom:5 },
  missingEmoji:  { fontSize:14 },
  missingName:   { flex:1, fontSize:10, color:theme.textSec },
  missingPrice:  { fontSize:11, color:theme.text },
  buyBtn:        { borderWidth:0.5, borderColor:theme.accent, paddingVertical:7, alignItems:'center', marginTop:6 },
  buyBtnText:    { fontSize:8, letterSpacing:2, color:theme.accent, textTransform:'uppercase' },

  infoBox:       { backgroundColor:theme.bgCard, borderRadius:radius.sm, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md, marginTop:spacing.sm },
  infoBoxText:   { fontSize:11, color:theme.textFaint, lineHeight:18, textAlign:'center' },

  tipsCard:      { backgroundColor:theme.bgCard, borderRadius:radius.md, borderWidth:0.5, borderColor:theme.bgBorder, padding:spacing.md, marginBottom:spacing.md },
  tipRow:        { paddingVertical:7, borderBottomWidth:0.5, borderBottomColor:theme.bgBorder },
  tipText:       { fontSize:12, color:theme.textSec, letterSpacing:0.3 },

  packLightCard: { backgroundColor:theme.accentGhost, borderRadius:radius.md, borderWidth:0.5, borderColor:theme.textFaint, padding:spacing.md },
  packLightTitle:{ fontSize:11, color:theme.accent, letterSpacing:0.5, fontWeight:'500', marginBottom:8 },
  packLightBody: { fontSize:11, color:theme.textFaint, lineHeight:20 },

  // ═══ NOUVEAU CHECKLIST PREMIUM (À NE PAS OUBLIER) ═══
  // Hero card avec progression
  // v1.12.135 — Carte plage de temperature pour la duree du sejour
  tempRangeCard:        { backgroundColor:'#FFF9E6', borderWidth:1, borderColor:'#F5DEB3', borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.md },
  tempRangeHeader:      { flexDirection:'row', alignItems:'center', gap: 14 },
  tempRangeIcon:        { fontSize: 36 },
  tempRangeBadge:       { fontSize: 9, color:'#B8860B', letterSpacing:1.5, fontWeight:'700', marginBottom:4 },
  tempRangeTitle:       { fontSize: 18, color:'#1A1A1A', fontWeight:'700', letterSpacing: 0.3 },
  tempRangeSub:         { fontSize: 11, color:'#7A6F5C', marginTop:3 },
  checklistHero:        { backgroundColor:'#1A1A1A', borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.md, shadowColor:'#000', shadowOpacity:0.12, shadowRadius:12, shadowOffset:{ width:0, height:4 }, elevation:4 },
  checklistHeroTop:     { flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 },
  checklistHeroBadge:   { fontSize: 10, color:'#FCD34D', letterSpacing:1.5, fontWeight:'700', marginBottom:6 },
  checklistHeroTitle:   { fontSize: 22, color:theme.bgCard, fontWeight:'700', letterSpacing: 0.3 },
  checklistHeroSub:     { fontSize: 11, color:'#B3B3B3', marginTop:4, lineHeight: 16 },
  checklistHeroCircle:  { width:56, height:56, borderRadius:28, borderWidth:2, borderColor:'#FCD34D', alignItems:'center', justifyContent:'center' },
  checklistHeroCircleNum:{ fontSize:16, color:'#FCD34D', fontWeight:'700' },
  checklistProgressBar: { height:6, backgroundColor:'rgba(255,255,255,0.12)', borderRadius:3, overflow:'hidden' },
  checklistProgressFill:{ height:'100%', backgroundColor:'#FCD34D', borderRadius:3 },

  // Alerte manque dans valise
  wardrobeAlertCard:    { backgroundColor:'#FEF3C7', borderRadius:radius.md, padding:spacing.md, marginBottom:spacing.md, borderWidth:1, borderColor:'#FCD34D' },
  wardrobeAlertHeader:  { flexDirection:'row', alignItems:'flex-start', gap:10, marginBottom:10 },
  wardrobeAlertIcon:    { fontSize:20, marginTop:2 },
  wardrobeAlertTitle:   { fontSize:13, fontWeight:'700', color:'#92400E', letterSpacing:0.3 },
  wardrobeAlertSub:     { fontSize:11, color:'#78350F', marginTop:2, lineHeight:15 },
  wardrobeAlertChips:   { flexDirection:'row', flexWrap:'wrap', gap:6, marginBottom:10 },
  wardrobeAlertChip:    { backgroundColor:theme.bgCard, paddingHorizontal:10, paddingVertical:5, borderRadius:radius.pill, borderWidth:1, borderColor:'#FCD34D' },
  wardrobeAlertChipText:{ fontSize:11, color:'#92400E', fontWeight:'600' },
  wardrobeAlertCTA:     { alignSelf:'flex-start', paddingHorizontal:14, paddingVertical:8, backgroundColor:'#92400E', borderRadius:radius.pill },
  wardrobeAlertCTAText: { fontSize:11, color:'#fff', fontWeight:'600', letterSpacing:0.3 },

  // Règle des jours (simplifiée et stylée)
  ruleCard:             { backgroundColor:'#F0FDF4', borderRadius:radius.md, padding:spacing.md, marginBottom:spacing.md, borderWidth:1, borderColor:'#86EFAC' },
  ruleCardTitle:        { fontSize:12, color:'#14532D', fontWeight:'700', letterSpacing:0.3, marginBottom:8 },
  ruleRow:              { flexDirection:'row', alignItems:'center', gap:6, marginBottom:6 },
  ruleNum:              { fontSize:20, fontWeight:'800', color:'#15803D' },
  ruleLabel:            { fontSize:11, color:'#15803D', marginLeft:3, marginRight:3 },
  ruleDivider:          { fontSize:14, color:'#86EFAC', marginHorizontal:3 },
  ruleFoot:             { fontSize:10, color:'#166534', lineHeight:14 },

  // AI Tips (bons plans)
  aiTipsCard:           { backgroundColor:theme.bgCard, borderRadius:radius.lg, padding:spacing.lg, marginBottom:spacing.md, borderWidth:1, borderColor:'#E5E5E5', shadowColor:'#000', shadowOpacity:0.04, shadowRadius:8, shadowOffset:{ width:0, height:2 }, elevation:1 },
  aiTipsHeader:         { flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:4 },
  aiTipsBadge:          { fontSize:9, color:'#8B5CF6', letterSpacing:1.5, fontWeight:'700', marginBottom:3 },
  aiTipsTitle:          { fontSize:15, fontWeight:'700', color:theme.text, letterSpacing:0.3 },
  aiTipsSub:            { fontSize:11, color:theme.textFaint, marginTop:2, lineHeight:15 },
  aiTipsBtn:            { backgroundColor:'#8B5CF6', paddingHorizontal:16, paddingVertical:10, borderRadius:radius.pill, minWidth:90, alignItems:'center', justifyContent:'center' },
  aiTipsBtnText:        { fontSize:11, color:'#fff', fontWeight:'700', letterSpacing:0.5 },
  aiTipsErr:            { fontSize:11, color:'#DC2626', marginTop:10, padding:10, backgroundColor:'#FEE2E2', borderRadius:6 },
  aiTipRow:             { flexDirection:'row', gap:10, paddingVertical:10, borderBottomWidth:0.5, borderBottomColor:'#F0F0F0' },
  aiTipIcon:            { fontSize:20, width:28, textAlign:'center' },
  aiTipCat:             { fontSize:9, color:'#8B5CF6', letterSpacing:1, fontWeight:'700', marginBottom:3 },
  aiTipText:            { fontSize:12, color:theme.text, lineHeight:17 },

  // Sections catégorisées
  catSection:           { backgroundColor:theme.bgCard, borderRadius:radius.lg, padding:spacing.md, marginBottom:spacing.md, borderWidth:1, borderColor:'#F0F0F0' },
  catSectionHeader:     { flexDirection:'row', alignItems:'center', gap:12, marginBottom:10, paddingBottom:10, borderBottomWidth:0.5, borderBottomColor:'#F0F0F0' },
  catSectionIcon:       { width:40, height:40, borderRadius:10, borderWidth:1, alignItems:'center', justifyContent:'center' },
  catSectionTitle:      { fontSize:13, fontWeight:'700', color:theme.text, letterSpacing:0.3 },
  catSectionCount:      { fontSize:10, fontWeight:'600', marginTop:2, letterSpacing:0.3 },
  catSectionList:       { },
  checkRow:             { flexDirection:'row', alignItems:'center', gap:11, paddingVertical:9 },
  checkBox:             { width:22, height:22, borderRadius:6, borderWidth:1.5, borderColor:'#D1D5DB', backgroundColor:'#fff', alignItems:'center', justifyContent:'center' },
  checkBoxTick:         { color:'#fff', fontSize:13, fontWeight:'800' },
  checkText:            { flex:1, fontSize:13, color:theme.text },
  checkTextDone:        { color:theme.textFaint, textDecorationLine:'line-through' },

  fieldLabel:    { fontSize:9, letterSpacing:3, color:theme.textFaint, textTransform:'uppercase', marginBottom:6, marginTop:spacing.md },
  input:         { backgroundColor:theme.bgCard, borderRadius:radius.sm, borderWidth:0.5, borderColor:theme.bgBorder, paddingHorizontal:spacing.md, paddingVertical:10, fontSize:12, color:theme.textSec, letterSpacing:0.3 },

  styleGrid:     { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:4 },
  styleChip:     { paddingHorizontal:12, paddingVertical:8, borderRadius:radius.pill, borderWidth:0.5, borderColor:theme.bgBorder, backgroundColor:theme.bgCard, flexDirection:'row', alignItems:'center', gap:5 },
  styleChipActive:{ borderColor:theme.accent, backgroundColor:theme.accentGhost },
  styleChipIcon: { fontSize:14 },
  styleChipLabel:{ fontSize:9, letterSpacing:1.5, color:theme.textFaint, textTransform:'uppercase' },
  styleChipLabelActive:{ color:theme.accent },

  weatherGrid:   { flexDirection:'row', flexWrap:'wrap', gap:8 },
  weatherChip:   { paddingHorizontal:10, paddingVertical:8, borderRadius:radius.sm, borderWidth:0.5, borderColor:theme.bgBorder, backgroundColor:theme.bgCard, alignItems:'center', minWidth:'30%' },
  weatherChipActive:{ borderColor:theme.accent, backgroundColor:theme.accentGhost },
  weatherChipIcon:{ fontSize:16, marginBottom:3 },
  weatherChipLabel:{ fontSize:9, letterSpacing:1.5, color:theme.textFaint, textTransform:'uppercase' },
  weatherChipLabelActive:{ color:theme.accent },
  weatherChipTemp:{ fontSize:8, color:theme.textFaint, marginTop:2 },

  // ═══════════ TRAVEL LOOKS PREMIUM (harmonisé avec Dressing/Looks) ═══════════
  travelHub:          { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, paddingBottom: spacing.md, borderBottomWidth: 1, borderBottomColor: theme.bgBorder },
  travelHubLeft:      { flex: 1, paddingRight: spacing.md },
  travelHubBadge:     { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  travelHubTitle:     { fontSize: 22, fontWeight: '300', color: theme.text, letterSpacing: 0.3, marginBottom: 4 },
  travelHubSub:       { fontSize: 11, color: theme.textFaint, lineHeight: 15 },
  travelHubStat:      { alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, backgroundColor: '#F8F6F0', borderRadius: radius.md, borderWidth: 1, borderColor: '#E8E0C8', minWidth: 80 },
  travelHubStatNum:   { fontSize: 22, fontWeight: '600', color: '#B8860B' },
  travelHubStatLabel: { fontSize: 8, letterSpacing: 1, color: theme.textFaint, textTransform: 'uppercase', marginTop: 2 },

  travelSectionHeader:{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.md, paddingHorizontal: 4 },
  travelSectionIcon:  { fontSize: 20 },
  travelSectionTitle: { fontSize: 14, fontWeight: '700', color: theme.text, letterSpacing: 0.3 },
  travelSectionSub:   { fontSize: 10, color: theme.textFaint, marginTop: 2 },

  // ─── Chips filtre occasion pour Travel ───
  travelOccWrap:       { marginBottom: spacing.md },
  travelOccRow:        { gap: 8, paddingHorizontal: 4 },
  travelOccChip:       { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.pill, borderWidth: 1.5, borderColor: theme.bgBorder, backgroundColor: theme.bgCard },
  travelOccChipActive: { borderColor: theme.text, backgroundColor: theme.text },
  travelOccLabel:      { fontSize: 11, letterSpacing: 0.5, color: theme.textSec, fontWeight: '500' },
  travelOccLabelActive:{ color: '#fff' },

  travelEmptyState:   { alignItems: 'center', padding: spacing.xl, borderRadius: radius.lg, backgroundColor: '#F9F7F3', borderWidth: 1, borderColor: theme.bgBorder, borderStyle: 'dashed', marginVertical: spacing.lg },
  travelEmptyIcon:    { fontSize: 48, marginBottom: spacing.md },
  travelEmptyTitle:   { fontSize: 16, fontWeight: '500', color: theme.text, marginBottom: 6 },
  travelEmptyDesc:    { fontSize: 12, color: theme.textFaint, textAlign: 'center', lineHeight: 18, marginBottom: spacing.lg },
  travelEmptyCta:     { paddingHorizontal: spacing.xl, paddingVertical: 10, backgroundColor: theme.text, borderRadius: radius.pill },
  travelEmptyCtaText: { color: theme.bgCard, fontSize: 12, fontWeight: '600', letterSpacing: 0.3 },

  // MixCombo pour voyage (articles à shopper)
  travelMixCombo:     { backgroundColor: theme.bgCard, borderRadius: radius.lg, overflow: 'hidden', marginBottom: spacing.lg, borderWidth: 1, borderColor: theme.bgBorder },
  travelMixHeader:    { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md, padding: spacing.md, borderBottomWidth: 1, borderBottomColor: theme.bgBorder },
  travelMixLabel:     { fontSize: 15, fontWeight: '600', color: theme.text, letterSpacing: 0.2 },
  travelMixMeta:      { fontSize: 11, color: theme.textSec, marginTop: 6, lineHeight: 15 },
  travelMixPriceBadge:{ backgroundColor: theme.text, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.sm, alignItems: 'center' },
  travelMixPriceLabel:{ fontSize: 7, letterSpacing: 1, color: '#FFD700', fontWeight: '700', marginBottom: 1 },
  travelMixPrice:     { fontSize: 13, fontWeight: '700', color: theme.bgCard },

  travelMixItemsRow:  { gap: 10, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  travelMixItem:      { alignItems: 'center', width: 90 },
  travelMixItemBox:   { width: 84, height: 100, borderRadius: radius.sm, overflow: 'hidden', backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: theme.bgBorder, position: 'relative' },
  travelMixItemBoxNew:{ borderColor: '#D4AF37', borderWidth: 2 },
  travelMixItemImg:   { width: '100%', height: '100%', resizeMode: 'cover' },
  travelMixItemPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  travelMixNewBadge:  { position: 'absolute', top: 4, right: 4, backgroundColor: '#D4AF37', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 4 },
  travelMixNewBadgeText: { fontSize: 7, fontWeight: '700', color: theme.bgCard, letterSpacing: 0.3 },
  travelMixItemName:  { fontSize: 10, color: theme.text, marginTop: 6, textAlign: 'center', fontWeight: '500' },
  travelMixItemPrice: { fontSize: 11, fontWeight: '700', color: '#D4AF37', marginTop: 2 },

  travelMixCta:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, backgroundColor: '#F9F7F3', borderTopWidth: 1, borderTopColor: theme.bgBorder },
  travelMixCtaText:   { fontSize: 13, fontWeight: '600', color: theme.text, letterSpacing: 0.2 },
  travelMixCtaArrow:  { fontSize: 16, color: theme.text, fontWeight: '700' },

  // ═══ Bouton régénération looks voyage ═══
  regenLooksBtn:    { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#FFF9E6', borderRadius: radius.pill, borderWidth: 1, borderColor: '#F5DEB3', paddingHorizontal: spacing.lg, paddingVertical: 10, marginBottom: spacing.lg, alignSelf: 'stretch', justifyContent: 'center' },
  regenLooksIcon:   { fontSize: 16 },
  regenLooksText:   { flex: 1, fontSize: 12, fontWeight: '700', color: '#B8860B', letterSpacing: 0.3, textAlign: 'center' },
  regenLooksArrow:  { fontSize: 14, color: '#B8860B', fontWeight: '700' },

  travelAddBtn:       { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10, backgroundColor: theme.text, borderRadius: radius.pill },
  travelAddBtnPlus:   { fontSize: 16, color: '#FFD700', fontWeight: '700' },
  travelAddBtnText:   { fontSize: 12, color: theme.bgCard, fontWeight: '600', letterSpacing: 0.3 },
  packedItemAiDot:    { position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, borderRadius: 9, backgroundColor: '#FFD700', alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: theme.bgCard },
  packedItemAiText:   { fontSize: 9 },

  // ═══════════ NOUVELLE LISTE VOYAGES PREMIUM ═══════════
  topbarBadge:        { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 2 },
  titlePremium:       { fontSize: 20, fontWeight: '400', color: theme.text, letterSpacing: 0.3 },
  addBtnPremium:      { width: 40, height: 40, borderRadius: 20, backgroundColor: theme.text, alignItems: 'center', justifyContent: 'center' },
  addBtnPremiumPlus:  { fontSize: 22, color: '#FFD700', fontWeight: '300', lineHeight: 26 },

  // ═══ Hero "Votre styliste de voyage" — adapté mode sombre ═══
  // Mode clair : fond crème pâle + bordure dorée (look chaleureux)
  // Mode sombre : fond surface sombre + accent doré (lisibilité + cohérence palette)
  travelListHero:     { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginHorizontal: spacing.xl, marginTop: spacing.md, marginBottom: spacing.lg, backgroundColor: dark ? theme.bgSurface : '#FFF9E6', borderRadius: radius.lg, borderWidth: 1, borderColor: dark ? '#3A2F1A' : '#F5DEB3', padding: spacing.lg, overflow: 'hidden' },
  travelListHeroIcon: { width: 64, height: 64, borderRadius: 32, backgroundColor: theme.bgCard, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: dark ? '#3A2F1A' : '#F5DEB3' },
  travelListHeroContent:{ flex: 1 },
  travelListHeroTitle:{ fontSize: 14, fontWeight: '700', color: dark ? '#D4AF72' : '#B8860B', marginBottom: 4, letterSpacing: 0.2 },
  travelListHeroSub:  { fontSize: 11, color: dark ? '#A89060' : '#8B7500', lineHeight: 16 },

  travelListStatsRow: { flexDirection: 'row', alignItems: 'center', marginHorizontal: spacing.xl, marginBottom: spacing.lg, backgroundColor: theme.bgCard, borderRadius: radius.md, borderWidth: 1, borderColor: theme.bgBorder, paddingVertical: spacing.md, paddingHorizontal: spacing.md },
  travelListStatCell: { flex: 1, alignItems: 'center' },
  travelListStatDiv:  { width: 1, height: 30, backgroundColor: theme.bgBorder },
  travelListStatNum:  { fontSize: 18, fontWeight: '600', color: theme.text },
  travelListStatLbl:  { fontSize: 8, letterSpacing: 1.5, color: theme.textFaint, textTransform: 'uppercase', marginTop: 3 },

  travelListSection:  { paddingHorizontal: spacing.xl },
  travelListSectionHeader:{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.md },
  travelListSectionIcon:{ fontSize: 20 },
  travelListSectionTitle:{ fontSize: 14, fontWeight: '700', color: theme.text, letterSpacing: 0.3 },
  travelListSectionSub:{ fontSize: 10, color: theme.textFaint, marginTop: 2 },

  travelListEmpty:    { alignItems: 'center', padding: spacing.xl, borderRadius: radius.lg, backgroundColor: '#F9F7F3', borderWidth: 1, borderColor: theme.bgBorder, borderStyle: 'dashed', marginBottom: spacing.lg },
  travelListEmptyIcon:{ fontSize: 48, marginBottom: spacing.md },
  travelListEmptyTitle:{ fontSize: 16, fontWeight: '500', color: theme.text, marginBottom: 6 },
  travelListEmptyDesc:{ fontSize: 12, color: theme.textFaint, textAlign: 'center', lineHeight: 18, marginBottom: spacing.lg },
  travelListEmptyCta: { paddingHorizontal: spacing.xl, paddingVertical: 12, backgroundColor: theme.text, borderRadius: radius.pill },
  travelListEmptyCtaText:{ color: theme.bgCard, fontSize: 12, fontWeight: '700', letterSpacing: 0.3 },

  tripCardPremium:    { flexDirection: 'row', alignItems: 'stretch', backgroundColor: theme.bgCard, borderRadius: radius.lg, borderWidth: 1, borderColor: theme.bgBorder, marginBottom: spacing.md, overflow: 'hidden' },
  tripCardPremiumMain:{ flex: 1, flexDirection: 'row', alignItems: 'center', padding: spacing.md },
  tripCardPremiumLeft:{ alignItems: 'center', justifyContent: 'center', marginRight: spacing.md, position: 'relative' },
  tripCardPremiumFlag:{ fontSize: 38 },
  tripCardPremiumStyleDot:{ position: 'absolute', bottom: 0, right: -2, width: 14, height: 14, borderRadius: 7, borderWidth: 2, borderColor: theme.bgCard },
  tripCardPremiumInfo:{ flex: 1 },
  tripCardPremiumTitleRow:{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2 },
  tripCardPremiumName:{ fontSize: 15, fontWeight: '700', color: theme.text, letterSpacing: 0.2, flex: 1 },
  tripCardPremiumReadyBadge:{ width: 18, height: 18, borderRadius: 9, backgroundColor: '#4CAF50', alignItems: 'center', justifyContent: 'center' },
  tripCardPremiumReadyText:{ color: theme.bgCard, fontSize: 10, fontWeight: '700' },
  tripCardPremiumDest:{ fontSize: 11, color: theme.textSec, marginBottom: 2 },
  tripCardPremiumDates:{ fontSize: 10, color: theme.textFaint, marginBottom: 6 },
  tripCardPremiumMeta:{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  tripCardPremiumPill:{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, backgroundColor: '#F5F3EE', borderRadius: radius.pill, borderWidth: 1, borderColor: '#E8E0C8' },
  tripCardPremiumPillIcon:{ fontSize: 10 },
  tripCardPremiumPillText:{ fontSize: 9, color: theme.textSec, fontWeight: '600' },
  tripCardPremiumRight:{ alignItems: 'center', justifyContent: 'center', minWidth: 52, paddingLeft: spacing.sm },
  tripCardPremiumCount:{ fontSize: 20, fontWeight: '700', color: '#B8860B', lineHeight: 22 },
  tripCardPremiumCountLbl:{ fontSize: 8, letterSpacing: 1.5, color: theme.textFaint, textTransform: 'uppercase', marginTop: 2 },
  tripCardPremiumArrow:{ fontSize: 20, color: theme.textFaint, marginTop: 4 },
  tripCardPremiumActions: { flexDirection: 'column', borderLeftWidth: 1, borderLeftColor: theme.bgBorder },
  tripCardPremiumEdit: { width: 36, flex: 1, alignItems: 'center', justifyContent: 'center', borderBottomWidth: 1, borderBottomColor: theme.bgBorder },
  tripCardPremiumEditText: { fontSize: 14, color: '#B8860B', fontWeight: '700' },
  tripCardPremiumDel: { width: 36, flex: 1, alignItems: 'center', justifyContent: 'center' },
  tripCardPremiumDelText:{ fontSize: 14, opacity: 0.5 },

  // ═══ Card "Nouveau voyage" en bas de liste — adaptée mode sombre ═══
  // Mode clair : fond crème pâle dashé doré (incite l'action)
  // Mode sombre : fond surface sombre + bordure dorée subtile (lisibilité)
  newTripCardPremium: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, backgroundColor: dark ? theme.bgSurface : '#F9F7F3', borderRadius: radius.lg, borderWidth: 1, borderColor: dark ? '#3A2F1A' : '#E8E0C8', borderStyle: 'dashed', padding: spacing.lg, marginTop: spacing.sm, marginBottom: spacing.lg },
  newTripCardPremiumIcon:{ fontSize: 28, color: dark ? '#D4AF72' : '#B8860B', fontWeight: '300' },
  newTripCardPremiumTitle:{ fontSize: 13, fontWeight: '700', color: theme.text, letterSpacing: 0.2 },
  newTripCardPremiumSub:{ fontSize: 10, color: theme.textFaint, marginTop: 2 },
  newTripCardPremiumArrow:{ fontSize: 16, color: dark ? '#D4AF72' : '#B8860B', fontWeight: '700' },
});
